// Documentation source for the in-app help system.
// Edit chapters/sections here — the help drawer and /help page render from this.
// Keep field descriptions in sync with the actual forms.
//
// Sichtbarkeit: `access` auf Kapitel- oder Abschnittsebene blendet Inhalte aus,
// die der eingeloggte User nicht nutzen kann ('vm' | 'admin' | 'financial' | 'kubernetes').
import store from '@/state/store';

export const docs = [
  {
    id: 'getting-started',
    title: 'Erste Schritte',
    icon: 'ri-rocket-line',
    routes: ['^/$', '^/changelog'],
    summary: 'Überblick über den vManager und die wichtigsten Bereiche.',
    sections: [
      {
        heading: 'Willkommen',
        body: `Der vManager ist deine zentrale Oberfläche zur Verwaltung der virtuellen Infrastruktur:
VMs erstellen und überwachen, Konsole nutzen, Backups wiederherstellen, ISO-Images verwalten und mehr.
Welche Bereiche und Funktionen du siehst, hängt von deinen Berechtigungen ab — auch diese Hilfe zeigt nur
Kapitel zu Funktionen, die du tatsächlich nutzen kannst.`,
      },
      {
        heading: 'Navigation',
        body: `Links findest du die Hauptbereiche als Icon-Spalte; beim Hover über ein Icon erscheint die volle Bezeichnung.
Das Logo oben links bringt dich jederzeit zur Startseite zurück. Die VM-Seiten beziehen ihre Daten über einen
Live-Stream — Status, Auslastung und Konfigurationsänderungen erscheinen ohne manuelles Aktualisieren.`,
      },
      {
        heading: 'Tutorials (Schulungsvideos)',
        body: `Das <b>Play-Symbol</b> (Tooltip «Tutorials») in der Kopfzeile, zwischen Support und Hilfe, öffnet die Schulungsvideos als Vollbild-Overlay.
Die Seite dahinter bleibt unverändert: Mit dem Kreuz oben rechts schliesst du die Tutorials und bist wieder genau dort,
wo du warst. Links sind die Videos nach Themen gruppiert, in der Reihenfolge eines Lernpfads (von «Einstieg» bis «Kosten und
Wiederverkauf»); «Nächstes Video» folgt derselben Reihenfolge. Es ist immer nur ein Thema aufgeklappt; ein Klick auf ein
anderes Thema klappt das bisherige zu. Rechts läuft das gewählte Video.
Videos starten nie von selbst, sondern erst mit Klick auf Play. Solange ein Video läuft, wird die Themenliste links schmaler,
damit das Video mehr Platz hat; bei Pause und nach dem Ende ist sie wieder so breit wie vorher. Alles ausser dem Video ist dabei unscharf, damit
nichts ablenkt: Bewegst du die Maus über die Themenliste oder den Text unter dem Video, wird der Bereich scharf, und er wird wieder
unscharf, wenn die Maus dort länger stillsteht. Pausierst du das Video, ist alles scharf. Das Standbild des Videos ist unscharf,
solange es nicht läuft (vor dem Start, bei Pause und am Ende); nur ein laufendes Video ist klar zu sehen. Die Bedienleiste unten bleibt dabei scharf. Nach dem Ende erscheint ein pulsierender Button «Zum nächsten Video».
Dein <b>Fortschritt</b> wird pro Video in Prozent gespeichert (auch geräteübergreifend, in deinem Benutzerkonto): bei jedem
Video siehst du, wie viel du schon angeschaut hast, und bei jedem Thema den Durchschnitt seiner Videos als Total. Videos,
die noch nicht veröffentlicht sind («Bald»), zählen im Total nicht mit. Mit «Zurücksetzen» setzt du ein Video wieder auf 0 %.
Der Stand wird während des Schauens laufend gespeichert (auch beim Pausieren, Wechseln, Schliessen und wenn du die Seite verlässt).
Ein angefangenes Video startet dort, wo du aufgehört hast. Hast du ein Video noch nie oder bis zum Ende geschaut, startet es von vorn.
Schaust du ein fertig geschautes Video erneut, bleibt es bei 100 %, bis du mindestens ein Viertel davon gesehen hast; ab dann zählt der Fortschritt wieder von vorn.
Wird ein Video neu aufgenommen, steht es wieder bei 0 %; hast du die frühere Version gesehen, zeigt es bei Video und Thema
den Hinweis <b>«Aktualisiert»</b>, bis du die neue Version begonnen hast.
Zu den Videos kann eine <b>Hintergrundmusik</b> laufen: Sie wird beim Sprechen automatisch leiser. Mit dem Schalter
<b>«Musik an/aus»</b> neben dem Titel schaltest du sie ab; die Einstellung merkt sich dein Browser.
Mit den Schaltflächen <b>0,75×, 1×, 1,25×, 1,5× und 2×</b> neben dem Titel stellst du ein, wie schnell das Video läuft (Standard ist 1×).
Die Musik läuft mit derselben Geschwindigkeit, und die Wahl gilt für alle Videos; auch sie merkt sich dein Browser.
Hast du noch nie ein Tutorial angeschaut, gilt das als erster Login: Dann öffnen sich die Tutorials beim Betreten der
Anwendung automatisch mit dem Willkommensvideo (nicht für Admins).`,
      },
      {
        heading: 'Hilfe öffnen',
        body: `Es gibt drei Wege zur Hilfe:
<ul class="mb-0 ps-3">
  <li>Das <code>?</code>-Symbol oben rechts in der Kopfzeile öffnet den Hilfe-Drawer.</li>
  <li>Tastenkürzel <code>?</code> (ausserhalb von Eingabefeldern) öffnet/schliesst den Drawer.</li>
  <li>Jedes Formularfeld hat ein kleines <code>?</code>-Icon mit Kurzbeschreibung — Klick öffnet das passende Kapitel.</li>
</ul>
Der Drawer erkennt die aktuelle Seite und springt automatisch zum richtigen Kapitel.`,
      },
      {
        heading: 'Benutzermenü und Darstellung',
        body: `Über das Benutzermenü oben rechts erreichst du Profil und Abmelden. Der Design-Schalter in der Kopfzeile
wechselt zwischen hellem und dunklem Modus; die Einstellung wird gemerkt und überlebt auch das Abmelden.`,
      },
      {
        heading: 'Benachrichtigungen (Mitteilungen des Betriebssystems)',
        body: `Unter <b>Benachrichtigungen</b> im Benutzermenü kannst du Mitteilungen des Betriebssystems aktivieren
(Mitteilungszentrale auf macOS, Benachrichtigungen unter Windows). Beim ersten Aktivieren fragt der Browser um Erlaubnis.
Pro Ereignis lässt sich wählen, ob eine Mitteilung kommt: eigene VM-Migration abgeschlossen oder fehlgeschlagen, eigene
Proxmox-Tasks abgeschlossen oder fehlgeschlagen, eigene Automatisierungen fehlgeschlagen oder verpasst (geplante
VM-Aktionen, auch bei einem übersprungenen Fehler in einer Abfolge) sowie (Admins) neue Probleme in der Kontrolle. Mitteilungen erscheinen
nur, wenn der vManager geöffnet, aber nicht im Vordergrund ist — sonst genügen die Meldungen oben rechts. Bei Automatisierungen
zeigt der vManager im Vordergrund ebenfalls eine Meldung oben rechts. Eine E-Mail gibt es dafür nicht. Ein Klick
öffnet die passende Seite. Die Einstellung gilt pro Browser bzw. Gerät; als installierte App (PWA) funktioniert es
ebenso, auf iPhone/iPad nur eingeschränkt. «Test-Mitteilung senden» zeigt eine Rückmeldung oder den technischen Fehler.
Auch wenn der Browser den Versand akzeptiert, kann das Betriebssystem die Anzeige unterdrücken: Prüfe die
Mitteilungen für deinen Browser in den Systemeinstellungen und den Fokus-/Nicht-stören-Modus. Die Berechtigung der
installierten App und die des Browsers können unterschiedlich sein.`,
      },
      {
        heading: 'Changelog (Admin)',
        access: 'admin',
        body: `Im Benutzermenü findest du als Admin den Punkt <b>Changelog</b>: eine chronologische Liste aller
nutzungsrelevanten Änderungen am vManager (neue Funktionen, Verhaltensänderungen, Bugfixes) mit Datum —
praktisch, um nach einem Update schnell zu sehen, was neu ist.`,
      },
    ],
  },
  {
    id: 'servers',
    title: 'Server / VMs',
    icon: 'ri-server-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/servers'],
    summary: 'Server-Übersicht, Suche, Kachel-/Tabellenansicht, VM-Details und Verwaltungsaktionen.',
    sections: [
      {
        heading: 'Übersicht',
        body: `Die Server-Seite zeigt links den Verzeichnisbaum (Cluster → Verzeichnisse → VMs).
Rechts erscheinen die Details der ausgewählten VM. Mit Rechtsklick auf ein Verzeichnis erhältst du das Kontextmenü
mit „VM erstellen", „Verzeichnis anlegen/umbenennen/löschen". Per Drag & Drop kannst du VMs in andere Verzeichnisse verschieben.`,
      },
      {
        heading: 'Ansichtsmodi im Baum',
        body: `Oben im Baum kannst du zwischen <b>Cluster-Ansicht</b> (gruppiert nach physischem Cluster)
und <b>Kombinierter Ansicht</b> (alle VMs flach in Verzeichnissen) umschalten.`,
      },
      {
        heading: 'Virtuelle Verzeichnisse «Kunden» und «Nexanet»',
        access: 'admin',
        body: `Für Admins gliedern zwei virtuelle Verzeichnisse die oberste Ebene: <b>Kunden</b> enthält alle
Verzeichnisse mit K-Nummer-Prefix (z.B. <code>K123456_...</code>), <b>Nexanet</b> alles übrige inklusive
VMs ohne Verzeichnis. Beide existieren nicht in Proxmox — sie sind reine Gliederung: nicht umbenenn- oder
löschbar, ein Klick filtert die Übersicht rechts auf alle enthaltenen VMs. Über das Kontextmenü lässt sich
ein Root-Verzeichnis anlegen; ob es unter Kunden oder Nexanet erscheint, entscheidet allein der Name.
Drag &amp; Drop auf «Nexanet» verschiebt auf die oberste Ebene; auf «Kunden» wird abgelehnt — die
Zuordnung folgt dem K-Prefix im Namen. Rechtsklick auf «Nexanet» → <b>VM erstellen</b> öffnet den Wizard mit
der Vorauswahl <b>Ohne Verzeichnis (oberste Ebene)</b>; diese Option steht Admins im Wizard generell zur Verfügung.`,
      },
      {
        heading: 'Virtuelle Verzeichnisse «Kunden» und «Eigene» (Wiederverkauf)',
        body: `Ist für dein Konto der <b>Wiederverkauf</b> aktiviert und mindestens ein End-Kunde erfasst, gliedern
zwei virtuelle Verzeichnisse den Inhalt deines Hauptverzeichnisses: <b>Kunden</b> enthält die Verzeichnisse, deren
Kennung (Teil vor dem ersten «_») einem auf der Seite <b>Endkunden</b> erfassten End-Kunden entspricht (ohne
Beachtung von Gross-/Kleinschreibung); <b>Eigene</b> alles übrige inklusive VMs direkt im Hauptverzeichnis.
Beide sind reine Gliederung: nicht umbenenn- oder löschbar, ein Klick filtert die Übersicht rechts. Drag &amp; Drop
auf «Eigene» verschiebt ins Hauptverzeichnis; auf «Kunden» wird abgelehnt — die Zuordnung folgt dem Namen.
Rechtsklick auf «Eigene» → <b>VM erstellen</b> öffnet den Wizard mit dem Hauptverzeichnis als Ziel (änderbar).`,
      },
      {
        heading: 'Kachel- und Tabellenansicht',
        body: `Rechts oben schaltest du per Icon zwischen <b>Kacheln</b> und <b>Tabelle</b> um; die Wahl wird gemerkt.
<p class="mb-1 mt-2"><b>Kacheln</b> zeigen pro VM Status, Auslastung, Netzwerk-Traffic (▼ Download / ▲ Upload) und den Schnellzugriff auf die Konsole.</p>
<p class="mb-1"><b>Tabelle</b> — sortierbar per Klick auf die Spaltenköpfe, Spalten wählbar über das „Spalten"-Menü (Auswahl wird gemerkt):</p>
<dl class="mb-0">
<dt>Name, VMID, Status, Cluster, Node, Verzeichnis, Kunde</dt><dd>Identifikation und Zuordnung. „Kunde" ist die K-Nummer aus dem Verzeichnisnamen.</dd>
<dt>Gast-OS</dt><dd>Das tatsächlich installierte Betriebssystem aus dem Guest Agent (z.B. „Ubuntu 24.04 LTS"); ohne Agent-Daten der in Proxmox konfigurierte OS-Typ.</dd>
<dt>IP-Adressen</dt><dd>Die IPs der VM aus dem Guest Agent (ohne Loopback/Link-Local). Bei mehreren IPs: erste + „+n", alle im Tooltip. Die Daten können bis zu 5 Minuten alt sein und setzen einen laufenden Guest Agent voraus.</dd>
<dt>CPU Cores, RAM, CPU %, RAM %</dt><dd>Konfigurierte Ressourcen und Live-Auslastung (Prozentwerte nur bei laufenden VMs).</dd>
<dt>Disks, Disk Lesen/Schreiben</dt><dd>Anzahl und Gesamtgrösse der Festplatten sowie aktuelle Lese-/Schreibrate.</dd>
<dt>Netzwerk (SDN), MAC-Adressen</dt><dd>Angeschlossene Netze (mit Alias und VLAN) und die MAC-Adressen der Netzwerkkarten.</dd>
<dt>Guest-Agent-Version (optional)</dt><dd>Über die Spaltenauswahl einblendbar: die zuletzt bekannte QEMU-Guest-Agent-Version aus dem Live-Stream. Ohne bekannte Version steht «–». Der Tooltip zeigt das erste Auftreten und die letzte Versionsänderung, nicht den letzten Online-Zeitpunkt. Windows- und Linux-Versionen haben unterschiedliche Nummerierungen und sind nicht direkt vergleichbar. Die Spalte wird bei Auswahl auch exportiert.</dd>
<dt>▼ Down / ▲ Up, Uptime</dt><dd>Aktueller Netzwerk-Traffic und Laufzeit.</dd>
</dl>
<p class="mb-0 mt-2"><b>Export:</b> Der Export-Button lädt die Tabelle als Excel-kompatible CSV-Datei herunter —
exportiert werden genau die sichtbaren Spalten in der aktuellen Sortierung.</p>`,
      },
      {
        heading: 'Suche und Filter',
        body: `Das Suchfeld über dem Baum filtert Baum und VM-Übersicht live. Mit <b>Leertaste</b> bestätigst du einen Begriff
als Token (Pill); mehrere Tokens wirken als UND-Verknüpfung. Während der Eingabe erscheinen Vorschläge und eine
Syntax-Hilfe (blendet sich nach kurzer Inaktivität automatisch aus). Solange Suchbegriffe vorhanden sind, werden alle
angezeigten Ordner und Gruppen automatisch aufgeklappt. Nach dem Leeren der Suche gilt wieder der manuelle Aufklappzustand.
Klickst du während einer Suche auf einen Ordner oder eine Gruppe, wird die gesamte Suche entfernt.
Der angeklickte Ordner und seine übergeordneten Ordner bleiben geöffnet; der Baum scrollt bei Bedarf zum angeklickten Ordner.
Die VM-Übersicht zeigt dann wieder alle VMs dieses Ordners inklusive Unterverzeichnissen.
<p class="mb-1 mt-2"><b>Filter-Syntax</b> — <code>schlüssel:wert</code>, optional mit führendem <code>/</code>:</p>
<dl class="mb-0">
<dt><code>/os:</code></dt><dd>Betriebssystem-Familie, z.B. <code>/os:linux</code>, <code>/os:windows</code>, <code>/os:bsd</code>.</dd>
<dt><code>/ip:</code></dt><dd>IP-Adresse aus dem Guest Agent oder aus den IP-Zuweisungen der VM (bei weitergeleiteten IPs auch die öffentliche Adresse), z.B. <code>/ip:192.168.1.10</code> oder mit Wildcard <code>/ip:10.0.*</code>. Auch die Freitext-Suche berücksichtigt beide Quellen; wird eine VM über eine IP gefunden, zeigt der Baum die passende IP als Badge.</dd>
<dt><code>/net:</code> bzw. <code>/bridge:</code></dt><dd>Netzwerk: Bridge-Name, <code>vlan123</code>, Kartentyp oder MAC-Adresse.</dd>
<dt><code>/cluster:</code>, <code>/node:</code></dt><dd>Cluster- bzw. Host-Name.</dd>
<dt><code>/status:</code></dt><dd>z.B. <code>running</code>, <code>stopped</code>.</dd>
<dt><code>/vmid:</code></dt><dd>Die numerische VM-ID.</dd>
</dl>
<p class="mb-0 mt-2"><b>Zusätze:</b> <code>!</code> vor einem Token negiert ihn (z.B. <code>!status:running</code>);
<code>*</code> und <code>?</code> sind Wildcards. Freitext ohne Schlüssel durchsucht Name, VMID, Netzwerke und IPs.
<code>Backspace</code> im leeren Feld holt das letzte Token zur Bearbeitung zurück, <code>Esc</code> schliesst die Vorschläge.</p>`,
      },
      {
        heading: 'VM-Aktionen',
        body: `Starten / Stoppen / Reboot / Suspend, Konsolen-Zugriff (noVNC), Klonen, Migrieren, Löschen.
Über das <b>Stift-Symbol</b> neben dem VM-Namen lässt sich die VM direkt umbenennen (gültiger DNS-Name erforderlich).
Boot-Reihenfolge ist per Drag & Drop bearbeitbar; CPU lässt sich über Cores pro Socket konfigurieren (max. 24 vCPUs gesamt, Admins 32; Sockets legen die Admins fest), RAM bis 64 GB (Admins 128 GB). Liegt eine VM bereits darüber, können die bestehenden Werte beibehalten oder reduziert werden.
<br><br><b>Klonen</b> (Rechtsklick in der Hierarchie) geht auch bei laufenden VMs — der Datenstand entspricht dann einem
abrupten Ausschalten, der Klon selbst bleibt ausgeschaltet. Nach „Klonen“ schliesst der Dialog und ein Warte-Fenster zeigt
den Fortschritt; danach wechselt die Ansicht direkt zur neuen VM. Das Warte-Fenster kann geschlossen werden — der Klon
läuft dann im Hintergrund weiter und meldet sich nach Abschluss. Im Dialog kann pro Netzwerk-Interface ein anderes Netz
(ggf. mit VLAN-ID) gewählt werden; umgehängte Interfaces erhalten am Klon eine neue MAC-Adresse.`,
      },
      {
        heading: 'VM-Detailansicht: Tabs',
        body: `<dl class="mb-0">
<dt>Übersicht</dt><dd>Status, Ressourcenverbrauch, Netzwerk- und Gastinformationen sowie Schnellaktionen.</dd>
<dt>Konfiguration</dt><dd>Aktuelle und ausstehende Proxmox-Konfiguration. Änderungen werden zunächst als Entwurf gesammelt und über <b>Speichern</b> gemeinsam angewendet.</dd>
<dt>Cloud-Init</dt><dd>Standardwerte und eigene Cloud-Init-Dateien bearbeiten. Der Tab ist erst nutzbar, wenn die VM ein Cloud-Init-Laufwerk besitzt.</dd>
<dt>Firewall</dt><dd>VM-Firewall, Regeln, IP-Sets und Sicherheitsoptionen verwalten. VM-Ebene und Netzwerkkarten-Ebene sind getrennte Schalter.</dd>
<dt>Statistik / System</dt><dd>Zeitreihen der VM und Informationen des Guest Agents, sofern dieser läuft.</dd>
<dt>Snapshots</dt><dd>Zeitpunktaufnahmen erstellen, zurücksetzen oder löschen. Snapshots ersetzen kein Backup.</dd>
<dt>Backups</dt><dd>Backups anzeigen, vollständige Restores starten und bei PBS-Backups einzelne Dateien oder Verzeichnisse laden.</dd>
<dt>Ereignisse</dt><dd>Asynchrone Proxmox-Tasks und deren Status, Exit-Status und Laufzeit. Die UTC-Zeitpunkte aus der API werden in der lokalen Zeitzone deines Browsers mit Sekunden angezeigt. Sommer- und Winterzeit werden automatisch berücksichtigt. Solange kein Abschlusszeitpunkt vorliegt, wird keine Dauer angezeigt.</dd>
</dl>`,
      },
      {
        heading: 'Befehlsausführung (Admin)',
        access: 'admin',
        body: `Im Tab <b>System</b> können Admins über den Guest Agent Befehle direkt im Gast ausführen —
inklusive vorbereiteter Vorlagen (z.B. Benutzer anlegen, Admin-Rechte vergeben; die Windows-Vorlagen funktionieren
sprachunabhängig). Voraussetzung: laufender Guest Agent und Konsolen-Berechtigung auf der VM.`,
      },
      {
        heading: 'Entwürfe und asynchrone Tasks',
        body: `Viele Änderungen werden zuerst als <b>Entwurf</b> gesammelt. So können mehrere Parameter in einem Vorgang geändert und gemeinsam gespeichert werden.
Nach dem Speichern arbeitet Proxmox oft einen asynchronen Task ab. Die VM-Ansicht kann während dieser Zeit noch den alten aktiven Wert und den neuen ausstehenden Wert anzeigen. Ein Task mit Status <code>OK</code> ist erfolgreich; bei Fehlern ist der Exit-Status ausschlaggebend.`,
      },
      {
        heading: 'Konsole und Guest Agent',
        body: `Die noVNC-Konsole funktioniert unabhängig davon, ob im Gast ein Agent installiert ist. Der Guest Agent liefert zusätzlich Gast-IP-Adressen, Dateisysteme, OS-Informationen und ermöglicht bestimmte Aktionen. Für Linux ist meist das Paket <code>qemu-guest-agent</code>, für Windows der QEMU Guest Agent erforderlich. Der Agent muss im Gastdienst laufen und in der VM-Konfiguration aktiviert sein.`,
      },
      {
        heading: 'Tasks-Drawer',
        body: `Unten am Bildschirmrand findest du das Tasks-Panel mit allen laufenden und kürzlich abgeschlossenen Aufgaben
(Erstellen, Migrieren, Klonen …). Der Zähler am Tab zeigt aktive Tasks. Zeitpunkte werden wie in den VM-Ereignissen in der lokalen Zeitzone deines Browsers angezeigt.`,
      },
      {
        heading: 'Mehrfachauswahl (Bulk-Modus)',
        body: `Über den Button <b>„Mehrfachauswahl"</b> über dem Baum aktivierst du Checkbox-Modus.
Klicke nacheinander auf VMs im Baum, um sie zur Auswahl hinzuzufügen — der rechte Bereich zeigt die Auswahl als Tabelle.
Verfügbare Bulk-Aktionen: <b>Starten</b>, <b>Herunterfahren</b>, <b>Neu starten</b>, <b>Pausieren</b>, <b>Fortsetzen</b>,
<b>Sofort stoppen</b>, <b>Hard Reset</b>, <b>In Verzeichnis verschieben</b>, <b>Löschen</b>. Jede Aktion wird nur auf die VMs angewendet, für die sie sinnvoll ist
(z.B. „Starten" nur auf gestoppte VMs, „Löschen" ebenfalls nur auf gestoppte VMs) — die Zahl auf dem Button zeigt das.
„In Verzeichnis verschieben" öffnet eine Ordner-Auswahl; bei VMs aus mehreren Clustern wird pro Cluster verschoben.
Beim Löschen wird zusätzlich zur normalen Bestätigung eine <b>zweite Sicherheitsabfrage</b> mit Auflistung der VM-Namen
eingeblendet, da der Vorgang nicht rückgängig gemacht werden kann. Aktionen laufen nacheinander; pro VM
siehst du Erfolg oder Fehlermeldung in der Tabelle.`,
      },
    ],
  },
  {
    id: 'vm-create',
    title: 'VM erstellen',
    icon: 'ri-add-circle-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/servers.*'],
    summary: 'Der VM-Erstellungs-Wizard Schritt für Schritt — inkl. Beschreibung aller Felder.',
    sections: [
      {
        heading: 'Aufruf',
        body: `Rechtsklick auf ein Verzeichnis im Server-Baum → „VM erstellen". Der Wizard erscheint im Detail-Bereich
und läuft in 4–5 Schritten: <b>Allgemein → Basis → Festplatten → Netzwerke → (Cloud-Init)</b>.`,
      },
      {
        heading: 'Schritt 1 — Allgemein: Felder',
        body: `<dl class="mb-0">
<dt>Name</dt>
<dd>Der Hostname der VM als DNS-Name. Erlaubt sind Buchstaben, Ziffern, Bindestriche und Punkte zur Trennung von Labels, z.B. <code>web-01.prod</code> oder <code>vm1.test.lab</code>. Jedes Label muss mit einem Buchstaben oder einer Ziffer beginnen und enden. Beim Erstellen mehrerer VMs muss der Name eine Zahl enthalten (z.B. <code>web001</code>), damit hochgezaehlt werden kann.</dd>
<dt>Anzahl VMs (1–10)</dt>
<dd>Erstellt mehrere VMs in einem Durchgang. Namen werden automatisch hochgezählt (Vorschau unter dem Namen). Bei Cloud-Init mit statischer IP wird auch die IPv4/IPv6 sequenziell fortgeschaltet.</dd>
<dt>Verzeichnis</dt>
<dd>Das Ziel-Verzeichnis (Resource Pool) im Cluster, in dem die VM angelegt wird. Bestimmt indirekt auch den Cluster.</dd>
<dt>Typ (Installationstyp)</dt>
<dd><b>Ab Nexanet Template</b>: klont aus einem Template im Resourcepool <code>Templates</code>. <b>Ab eigenem Template</b>: klont aus allen anderen Templates. <b>Manuelle Installation</b>: erzeugt eine leere VM und installiert ein OS aus einer ISO.</dd>
<dt>Template</dt>
<dd>Bei „Ab Nexanet Template" / „Ab eigenem Template": die Vorlage, aus der die VM erzeugt wird. Beide Listen sind alphabetisch nach Namen sortiert. Erkannte Sprachendungen mit Unterstrich oder Bindestrich wie <code>_DE</code>, <code>-EN</code>, <code>-de</code> oder <code>_De</code> werden in den Kacheln als Flagge unten rechts auf dem Betriebssystem-Symbol gezeigt; der Kachelname erscheint dann ohne diese Endung. In der Auswahlliste (ab 11 Templates) bleibt der vollständige Name stehen, die Flagge erscheint am Symbol des gewählten Templates. Gross-/Kleinschreibung spielt dabei keine Rolle.</dd>
<dt>Betriebssystem Typ</dt>
<dd>Bei manueller Installation: legt sinnvolle Defaults fest (BIOS, Treiber, TPM). Bei Windows wird TPM automatisch aktiviert.</dd>
<dt>ISO</dt>
<dd>Bei manueller Installation: das ISO-Image, das beim ersten Boot als CD-ROM gemountet wird.</dd>
</dl>`,
      },
      {
        heading: 'Schritt 2 — Basis: Felder',
        body: `<dl class="mb-0">
<dt>CPU — Sockets × Cores</dt>
<dd>Anzahl virtueller CPU-Sockel und Kerne pro Sockel. Beim Erstellen immer <code>1 Sockel × N Cores</code>, mindestens 2 und maximal 24 vCPUs (Admins: 32). Mehr benötigt? Bitte bei den Admins melden.</dd>
<dt>Arbeitsspeicher (GB)</dt>
<dd>RAM in Gigabyte, maximal 64 GB (Admins: 128 GB). Wird der VM fest zugewiesen.</dd>
<dt>TPM</dt>
<dd>Virtuelles TPM 2.0 für die VM. Erforderlich für Windows 11. Wird bei Windows-Templates automatisch gesetzt.</dd>
<dt>Nach Erstellung starten</dt>
<dd>Wenn aktiv, wird die VM nach erfolgreicher Erstellung sofort hochgefahren. Andernfalls bleibt sie gestoppt.</dd>
</dl>`,
      },
      {
        heading: 'Schritt 3 — Festplatten: Felder',
        body: `Pro Disk:
<dl class="mb-0">
<dt>Storage</dt>
<dd>Das Storage-Backend, auf dem die Disk liegen wird. Verfügbarkeit ist Cluster-abhängig.</dd>
<dt>Diskgrösse</dt>
<dd>Grösse in der gewählten Einheit. Bei Disks aus einem Template gilt ein Minimum (in Klammern angezeigt) — kleiner geht nicht.</dd>
<dt>Einheit</dt>
<dd>MiB / GiB / TiB. Auswahl ändert die Anzeige, nicht die tatsächliche Grösse rückwirkend.</dd>
</dl>
Mit „Disk hinzufügen" kannst du weitere Festplatten anhängen, mit dem Mülleimer wieder entfernen (Template-Disk lässt sich nicht entfernen).`,
      },
      {
        heading: 'Schritt 4 — Netzwerke: Felder',
        body: `Pro Netzwerk-Interface:
<dl class="mb-0">
<dt>Model</dt>
<dd>Netzwerkkartentyp. <b>virtio</b> (paravirtualisiert) ist empfohlen — schnellste Variante. <b>e1000</b> / <b>rtl8139</b> für ältere Gäste ohne virtio-Treiber.</dd>
<dt>Bridge / Netzwerk</dt>
<dd>Die virtuelle Bridge, an die die VM angeschlossen wird. Bestimmt das physische Netz / VLAN-Range.</dd>
<dt>VLAN-ID</dt>
<dd>Das Feld erscheint erst, wenn ein Netzwerk ausgewählt ist und dieses VLAN-aware ist. Dann ist eine VLAN-ID (1–4094) erforderlich. Ohne Netzwerkauswahl sowie bei nicht VLAN-aware oder unbekannten Netzwerken bleibt das Feld verborgen. Tags ausserhalb des erlaubten Bereichs werden beim Speichern verworfen.</dd>
</dl>
„Netzwerk hinzufügen" für weitere Interfaces.`,
      },
      {
        heading: 'Schritt 5 — Cloud-Init: Felder',
        body: `Bei Linux kann Cloud-Init auch bei einer manuellen ISO-Installation aktiviert werden; dafür wird ein Cloud-Init-Laufwerk benötigt. Modi: <b>Standard</b> (Felder unten), <b>Erweitert</b> (raw cloud-config YAML; beim Template-Klonen).
<dl class="mb-0">
<dt>Benutzer / Passwort</dt>
<dd>Initialer Benutzer und Passwort, das im Gast gesetzt wird. Falls leer, kommt aus dem Template oder es muss SSH-Key verwendet werden.</dd>
<dt>DNS-Domain</dt>
<dd>Suchdomain für den Resolver des Gasts (z.B. <code>example.local</code>).</dd>
<dt>DNS-Server</dt>
<dd>Ein oder mehrere DNS-Server, Leerzeichen-getrennt (z.B. <code>1.1.1.1 8.8.8.8</code>).</dd>
<dt>IPv4-Modus</dt>
<dd><b>DHCP</b>: automatisch. <b>Statisch</b>: Adresse + Prefix + Gateway angeben. <b>Skip</b>: IPv4 nicht konfigurieren.</dd>
<dt>IPv4-Adresse / Prefix / Gateway</dt>
<dd>Im statischen Modus. Bei <i>Anzahl VMs > 1</i> wird die Adresse pro VM automatisch hochgezählt; Vorschau erscheint unter dem Namen.</dd>
<dt>IPv6-Modus</dt>
<dd>Analog zu IPv4: DHCP, statisch oder Skip.</dd>
<dt>IPv6-Adresse / Prefix / Gateway</dt>
<dd>Im statischen Modus. Die letzte Hex-Gruppe wird bei mehreren VMs automatisch hochgezählt.</dd>
</dl>`,
      },
      {
        heading: 'Automatische VM-Standards',
        body: `<p>Neue VMs erhalten im Hintergrund einige feste Basiswerte:</p>
    <ul>
    <li>QEMU Guest Agent aktiviert</li>
    <li>CPU-Modell <code>host</code> mit deaktivierter Nested Virtualization (<code>flags=-nested-virt</code>) und Machine Type <code>q35</code></li>
    <li>Grafikkarte <code>virtio,memory=8</code></li>
    <li>Ballooning mit Minimum 75% des konfigurierten RAM</li>
    <li><code>allow-ksm=0</code>, damit Kernel Samepage Merging für die VM deaktiviert ist</li>
    <li>VM-Firewall aktiviert mit eingehender Standard-Policy <code>ACCEPT</code></li>
    </ul>
    Bei Windows kann Proxmox den q35-Machine-Type automatisch auf eine konkrete Version pinnen, zum Beispiel <code>pc-q35-11.0+pve2</code>. Das ist keine Abweichung und sollte nicht manuell auf <code>latest</code> geändert werden.`,
      },
      {
        heading: 'Validierung & Fehler',
        body: `Rote Felder sind Pflichtfelder oder enthalten ungültige Werte. Beim Klick auf <i>Weiter</i> bzw. <i>Erstellen</i>
prüft der Wizard alle Eingaben. Beim Erstellen mehrerer VMs werden die VMs nacheinander angelegt; bei einem Fehler bricht der Lauf ab.`,
      },
    ],
  },
  {
    id: 'console',
    title: 'VM-Konsole',
    icon: 'ri-terminal-box-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/servers/.*/console'],
    summary: 'noVNC-Konsole: Zugriff, Text einfügen und Tastaturlayout.',
    sections: [
      {
        heading: 'Öffnen',
        body: `Die Konsole öffnest du aus der VM-Detailansicht oder über das Konsolen-Icon in Kacheln/Tabelle
(öffnet ein eigenes Browserfenster). Sie funktioniert auch ohne Guest Agent — du siehst den Bildschirm der VM
ab BIOS/Bootloader, ideal auch für die OS-Installation.`,
      },
      {
        heading: 'Text einfügen (Zwischenablage)',
        body: `Über den <b>Einfügen</b>-Button kannst du Text aus der Zwischenablage in die Konsole tippen lassen —
praktisch für Passwörter oder lange Befehle, da die VNC-Konsole kein direktes Einfügen kennt.
Damit Sonderzeichen korrekt ankommen, muss das <b>Tastaturlayout des Gasts</b> stimmen:
wählbar sind <b>Schweiz (de-ch)</b>, <b>Deutschland (de-de)</b> und <b>US (en-us)</b>.
Das Layout wird automatisch vorerkannt (Schweizer Systeme werden auch dann erkannt, wenn der Browser nur „Deutsch" meldet)
und kann manuell umgestellt werden.`,
      },
      {
        heading: 'Hinweise',
        body: `Das eingestellte Layout muss dem im <i>Gast</i> konfigurierten Layout entsprechen — nicht dem deines
lokalen Rechners. Kommen falsche Zeichen an (z.B. <code>z</code>/<code>y</code> vertauscht), ist das Layout falsch gewählt.
Bei Login-Masken ohne sichtbare Eingabe (Passwortfelder) den Text einfügen und mit Enter bestätigen.`,
      },
    ],
  },
  {
    id: 'topologie',
    title: 'Topologie',
    icon: 'ri-node-tree',
    access: 'vm',
    routes: ['^/virtual-infrastructure/networks'],
    summary: 'Grafische Live-Ansicht der Netzwerke und VMs.',
    sections: [
      {
        heading: 'Überblick',
        body: `Die Topologie zeigt deine Netzwerke und die angeschlossenen VMs als grafische Live-Ansicht,
inklusive aktuellem Netzwerk-Traffic (▼ Download / ▲ Upload) pro VM. Die Daten kommen aus demselben
Live-Stream wie die Server-Seite und aktualisieren sich automatisch.`,
      },
    ],
  },
  {
    id: 'networks',
    title: 'SDN-Netzwerke',
    icon: 'ri-router-line',
    access: 'admin',
    routes: ['^/virtual-infrastructure/network-list'],
    summary: 'SDN-Netzwerke, VLANs, Subnetze und Kunden-Zuordnung (Admin).',
    sections: [
      {
        heading: 'Liste',
        body: `Die Netzwerkliste zeigt alle SDN-Netze mit VLAN-ID, Objektname, Alias, zugewiesenen Kunden und Subnetzen.
Ein Schild-Symbol markiert Netze mit aktivem <b>„Nur zugewiesene IPs"</b>-Flag.`,
      },
      {
        heading: 'Netz bearbeiten',
        body: `Der Dialog ist in zwei Tabs gegliedert:
<dl class="mb-0">
<dt>Kundenberechtigung</dt><dd>Alias und die berechtigten Kunden (numerische Kundennummern; die Umwandlung in LDAP-Gruppen passiert serverseitig). Hier sitzt auch der Schalter „Nur zugewiesene IPs".</dd>
<dt>Subnetze</dt><dd>Subnetze (CIDR) mit Gateway pro Netz hinterlegen und pflegen; die Liste ersetzt beim Speichern den bisherigen Stand.</dd>
</dl>
Die VLAN-ID ist bei bestehenden Netzen nicht mehr änderbar. Erstellen/Löschen/Ändern laufen als asynchrone Jobs —
die Liste aktualisiert sich nach Abschluss automatisch.`,
      },
      {
        heading: 'Aliasse für Benutzer',
        body: `Die hier gepflegten Alias-Namen sehen auch normale Benutzer: In der VM-Übersicht wird der Alias
neben dem technischen Bridge-Namen angezeigt — für Nicht-Admins allerdings nur bei Netzen, die ihrem Kunden
zugewiesen sind.`,
      },
    ],
  },
  {
    id: 'ip-assignments',
    title: 'IP-Zuweisungen',
    icon: 'ri-map-pin-line',
    access: 'admin',
    routes: ['^/virtual-infrastructure/ip-assignments'],
    summary: 'Interne IPs einem Kunden zuordnen, inkl. History (Admin).',
    sections: [
      {
        heading: 'Zuweisungen',
        body: `Ordne interne IPs eines SDN-Netzes zu — optional mit Kunde, VM-Referenz und Beschreibung.
Es gibt zwei Typen: <b>Öffentliche IP</b> (Standard, ohne Public-IP) und <b>Weitergeleitete IP</b>
(mit Pflichtfeld Public-IP, von der auf die interne IP geforwardet wird).
Beim Anlegen können mehrere IPs aufs Mal erfasst werden: als Liste (Komma/Leerzeichen getrennt) oder als
IPv4-Range wie <code>10.0.0.5-10.0.0.20</code> (Kurzform <code>10.0.0.5-20</code>) — Kunde, Typ und alle weiteren
Angaben gelten dann für alle IPs. Über die Checkboxen in der Tabelle lassen sich mehrere Zuweisungen
gesammelt löschen. Die Zuweisungen dienen als Grundlage für Verrechnung und Nachvollziehbarkeit.`,
      },
      {
        heading: 'Kunde, VM-Referenz und Verrechnung',
        body: `<dl class="mb-0">
<dt>Kundennummer</dt><dd>Optional; wenn gesetzt, 6-stellig. Kann beim Bearbeiten auch wieder entfernt werden. Beim Zuordnen wird geprüft, ob der Kunde Zugriff auf das SDN-Netz hat — fehlt er, kann der Zugriff direkt miterteilt werden; haben weitere Kunden Zugriff, erscheint ein Schutz-Hinweis.</dd>
<dt>VM-Referenz</dt><dd>Optional eine VM über die hierarchische Auswahl (Cluster/Verzeichnisse/VMs, mit Suche) wählen. Rein operative Info — nicht verrechnungsrelevant und ohne History; in der Tabelle als Link zur VM.</dd>
<dt>Verrechnung aktiv</dt><dd>Schalter pro Zuweisung; nur möglich, wenn eine Kundennummer gesetzt ist (ohne Kunde immer aus — beim Entfernen des Kunden wird die Verrechnung automatisch deaktiviert). Pausierte Zuweisungen sind in der Tabelle markiert; Änderungen am Schalter erzeugen eine neue History-Periode.</dd>
<dt>Nicht für VM-Nutzung</dt><dd>Markiert IPs, die keiner VM-Netzwerkkarte zugewiesen werden dürfen (z.B. Gateways, Reservierungen, Infrastruktur-IPs). Solche IPs erscheinen für normale Benutzer gar nicht in der VM-IP-Auswahl und werden auch für Admins abgelehnt; eine bestehende VM-Referenz bleibt beim Setzen unberührt.</dd>
</dl>`,
      },
      {
        heading: 'History',
        body: `Der History-Tab zeigt alle verrechnungsrelevanten Änderungen chronologisch — für Audit-Zwecke und die
rückwirkende Klärung, welchem Kunden eine IP zu einem Zeitpunkt zugeordnet war. Eine neue Periode entsteht bei
Änderung von Typ, Kunde, Public-IP oder Verrechnungsstatus; VM-Referenz und Beschreibung werden nicht historisiert.`,
      },
    ],
  },
  {
    id: 'vmware-migration',
    title: 'VMware Migration',
    icon: 'ri-download-cloud-2-line',
    access: 'admin',
    routes: ['^/virtual-infrastructure/vmware-migration'],
    summary: 'VMs von ESXi/vCenter nach Proxmox importieren (Admin).',
    sections: [
      {
        heading: 'Voraussetzung',
        body: `In Proxmox muss ein Storage vom Typ <code>esxi</code> eingebunden sein (Proxmox 8.2+), der sich mit dem
ESXi-Host bzw. vCenter verbindet. Ohne einen solchen Storage zeigt die Seite einen entsprechenden Hinweis.`,
      },
      {
        heading: 'Ablauf',
        body: `Die Seite listet pro Cluster alle migrationsbereiten VMs der ESXi-Storages (die Abfrage des ESXi kann
etwas dauern). <b>Importieren…</b> öffnet den Import-Dialog mit den vom ESXi gelesenen Metadaten: vorgeschlagene
Konfiguration, Disks, Netzwerkkarten und allfällige Warnungen. Vor dem Import wählst du Name, Ziel-Storage für die
Disks, Ziel-Netzwerk (Bridge) und das Ziel-Verzeichnis; optional startet die VM nach dem Import automatisch.
Liegen die Disks unterhalb von NAS5-MIGRATION, ist der <b>Direktimport via NFS</b> standardmässig aktiv — die Disks
werden dann direkt vom NFS-Storage gelesen statt über den ESXi (deutlich schneller).`,
      },
      {
        heading: 'Hinweise',
        body: `Der Import kopiert die Disks vom ESXi — das kann je nach Grösse lange dauern; der Fortschritt ist im
Tasks-Panel bzw. in den VM-Ereignissen sichtbar. Die Quell-VM auf dem ESXi sollte während des Imports ausgeschaltet
sein, damit die Daten konsistent sind. Warnungen aus den Metadaten (z.B. nicht unterstützte Features) unbedingt prüfen.`,
      },
    ],
  },
  {
    id: 'vm-migration',
    title: 'VM auf anderen Cluster migrieren',
    icon: 'ri-flight-takeoff-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/migrations'],
    summary: 'VMs live oder offline auf einen anderen Cluster verschieben — inkl. Warteschlange, Fortschritt und Nacharbeiten (experimentell).',
    sections: [
      {
        heading: 'Experimentelle Funktion',
        body: `Die Migration auf einen anderen Cluster ist noch nicht fertig entwickelt: Sie kann fehlschlagen, länger dauern
als erwartet oder Nacharbeiten erfordern. Vor dem Start muss im Dialog bestätigt werden, dass man das weiss und in Kauf
nimmt. Nur verwenden, wenn ein Ausfall oder Fehler der VM verkraftbar ist, und das Ergebnis danach prüfen.`,
      },
      {
        heading: 'Migration starten',
        body: `In den VM-Details startet <b>Auf anderen Cluster migrieren</b> (Flugzeug-Symbol) den Dialog — ebenso per
Rechtsklick auf die VM in der Hierarchie. Nach Wahl des
<b>Ziel-Clusters</b> ordnest du jedem Storage und jedem Netz der VM ein Ziel zu. Gibt es alle gleichnamig auf dem Ziel,
ist «Gleiche Storages verwenden» bzw. «Gleiche Netze verwenden» vorausgewählt. Zugeordnet wird pro Quell-Netz —
mehrere Netzwerkkarten am selben Netz landen auf demselben Ziel-Netz; VLAN-Tag, MAC-Adresse, Firewall-Flag und Modell
bleiben erhalten. Hinweise erscheinen bei abweichendem VLAN, fehlender VLAN-Unterstützung und bei Netzen mit
IP-Zuweisungspflicht. Die VM erhält auf dem Ziel immer die nächste freie VM-ID und bleibt im selben Verzeichnis.`,
      },
      {
        heading: 'Live oder offline',
        body: `Eine laufende VM wird standardmässig <b>live</b> migriert und läuft währenddessen weiter. Das geht nur mit
kompatibler CPU: Beim CPU-Typ <code>host</code> sieht die VM die echte Host-CPU — live ist dann nur möglich, wenn alle
Nodes des Ziel-Clusters dasselbe CPU-Modell haben wie der Quell-Node (die Prüfung ist nur für Admins möglich; sonst nur
offline). Generische Typen wie <code>x86-64-v2-AES</code> sind immer live-fähig, bei benannten Modellen muss die
Ziel-CPU das Modell unterstützen. Für eine <b>Offline-Migration</b> muss die VM ausgeschaltet sein — der Dialog bietet
«Jetzt herunterfahren» an und wartet, bis die VM gestoppt ist.`,
      },
      {
        heading: 'Voraussetzungen',
        body: `Nicht migriert werden können VMs mit <b>Snapshots</b> (vorher löschen), mit belegtem <b>CD-ROM-Laufwerk</b>
(ein ISO lässt sich im Dialog direkt auswerfen) oder solange die VM <b>gesperrt</b> ist (z.B. laufendes Backup) — der
Dialog warnt vorab. Cloud-Init ist kein Hindernis: Das Cloud-Init-Laufwerk wird mit den Disks migriert; eine
Cloud-Init-ISO des erweiterten Modus wird automatisch ausgeworfen und auf dem Ziel neu erzeugt (nicht selbst
auswerfen). Replikationsjobs werden erst beim Start erkannt und als Fehler gemeldet.`,
      },
      {
        heading: 'Warteschlange und Fortschritt',
        body: `Es wird immer nur eine VM gleichzeitig migriert; weitere Aufträge warten. Eine Migration kann Stunden dauern.
Abschluss und Fehler einer eigenen Migration erscheinen als Meldung oben rechts. Hat eine VM Migrationen bzw. offene
Aufträge, zeigt der Reiter <b>Migrationen</b> in den VM-Details Fortschritt, Verlauf und nach Erfolg eine Checkliste der
Nacharbeiten; ohne Migrationen ist der Reiter ausgeblendet.`,
      },
      {
        heading: 'Seite „Migrationen“ (Admin)',
        access: 'admin',
        body: `Die Seite <b>Migrationen</b> (Virtuelle Infrastruktur) zeigt die Warteschlange mit Position, laufende Migrationen mit
Phase, Fortschritt in Prozent, übertragenen GB, Rate (MB/s), geschätzter Restdauer und Dauer sowie den Verlauf mit
Filtern. Prozent und Restdauer sind eine <b>Schätzung</b> aus den Disk-Grössen (bei Live-Migration plus RAM) — leere
Bereiche werden evtl. nicht übertragen, geänderter RAM evtl. mehrfach; der Stand wird etwa alle 30 Sekunden aktualisiert.
Scheitert die Vorprüfung, erscheint der Grund in der Warteschlange und als Meldung.
Der Menüpunkt zeigt offene Aufträge (blau) und Fehler bzw. Warnungen der letzten 7 Tage (rot).
«Status unklar» bedeutet: seit über 5 Minuten keine Aktualisierung — Task in Proxmox prüfen. Die aufgeklappte Zeile
einer erfolgreichen Migration zeigt eine Checkliste der Nacharbeiten und pausiert auf Wunsch die Automatisierungen der
Quell-VM.`,
      },
      {
        heading: 'Nach der Migration',
        body: `Die VM erhält auf dem Ziel eine <b>neue VM-ID</b> und kommt ins selbe Verzeichnis (fehlende Stufen werden
angelegt; ein neues Verzeichnis ist für Kunden evtl. erst nach bis zu 15 Minuten sichtbar). Die <b>Quell-VM</b> wird nach
erfolgreicher Migration standardmässig <b>gelöscht</b> (Schalter im Dialog, Rückfrage beim Absenden; scheitert die
Migration, bleibt sie erhalten). Ist der Schalter aus, bleibt sie gestoppt erhalten, wird aber von Proxmox <b>gesperrt</b>
(lock: migrate): Sie lässt sich danach weder starten noch löschen oder bearbeiten — entsperren kann sie nur ein Superuser
(Admins kontaktieren). Bis zu ihrer Löschung wird sie weiter verrechnet. Nicht mit umgezogen werden IP-Zuweisungen bzw. IP-Filter, Automatisierungen und
Cloud-Init-Einstellungen — bitte auf der neuen VM prüfen bzw. neu setzen und die Automatisierungen der Quell-VM
pausieren (ein geplantes «Starten» würde sie sonst wieder hochfahren). Warnungen (z.B.
Verzeichnis oder HA auf dem Ziel nicht gesetzt) erfordern einen manuellen Eingriff. Nach einem Fehler kann auf dem
Ziel eine halbfertige VM zurückbleiben — Ziel-Cluster kontrollieren.`,
      },
    ],
  },
  {
    id: 'clusters',
    title: 'Clusters',
    icon: 'ri-stack-line',
    access: 'admin',
    routes: ['^/virtual-infrastructure/clusters'],
    summary: 'Live-Übersicht der Proxmox-Cluster (Admin).',
    sections: [
      {
        heading: 'Überblick',
        body: `Die Clusters-Seite zeigt alle Proxmox-Cluster mit Gesamt-CPU/RAM-Auslastung (Tachos mit Absolutwerten im Kopf).
Details pro Cluster — Nodes, Storages und weitere Infrastruktur-Informationen — erscheinen beim Aufklappen.
Die Daten kommen direkt aus dem Live-Stream und aktualisieren sich laufend; ein manueller Refresh ist nicht nötig.`,
      },
    ],
  },
  {
    id: 'vm-firewall',
    title: 'VM-Firewall',
    icon: 'ri-shield-keyhole-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/servers.*'],
    summary: 'Erklärung der Firewall-Optionen direkt in der VM-Konfiguration.',
    sections: [
      {
        heading: 'Überblick',
        body: `In der VM-Detailansicht kannst du die Proxmox-VM-Firewall steuern: globale Optionen, Regeln, IP-Filter je Interface und IP-Sets.
Die Schalter wirken zusätzlich zu deinen Regeln und öffnen/limitieren bestimmte Protokolle automatisch.`,
      },
      {
        heading: 'Netze mit IP-Zuweisungspflicht (assigned-ips-only)',
        body: `Hängt eine Netzwerkkarte an einem Netz mit IP-Zuweisungspflicht, erzwingt das System automatisch:
VM-Firewall aktiv, IP-Filter aktiv, <code>firewall=1</code> auf der NIC und ein IPSet <code>ipfilter-netX</code> mit
mindestens einer zugewiesenen IP (siehe Seite IP-Zuweisungen). Beim Erstellen, Klonen, Importieren oder Umhängen
einer NIC auf ein solches Netz muss deshalb mindestens eine IP gewählt werden — die Auswahl zeigt freie IPs,
bereits dieser VM zugewiesene und (für Admins) von anderen VMs übernehmbare.
Solange die NIC auf dem Netz hängt, können Firewall und IP-Filter nicht deaktiviert und der letzte
IPSet-Eintrag nicht entfernt werden; Einträge sind nur als einzelne IPs mit gültiger Zuweisung erlaubt.
Beim Löschen der VM werden ihre IP-Zuweisungen automatisch freigegeben (bleiben dem Kunden erhalten).`,
      },
      {
        heading: 'Optionale IP-Bindung auf normalen Netzen',
        body: `Auch auf SDN-Netzen <b>ohne</b> IP-Zuweisungspflicht können einer NIC IPs aus den IP-Zuweisungen
zugeordnet werden (beim Erstellen, Klonen, Importieren und in den NIC-Dialogen — dort als „optional" markiert).
Wählt man IPs, aktiviert der vManager beim Speichern automatisch die VM-Firewall, den IP-Filter und das
Firewall-Flag der NIC — die Karte darf dann nur noch mit den gewählten Quell-IPs senden, und die IPs werden
der VM zugeordnet (inkl. Verrechnung gemäss Zuweisung). Damit andere Netzwerkkarten ohne eigenes Filter-Set
dabei nicht blockiert werden, erhalten sie automatisch ein „alle IPs erlaubt"-Set (<code>0.0.0.0/0</code> und
<code>::/0</code>) — sichtbar und bei Bedarf durch echte IPs ersetzbar.`,
      },
      {
        heading: 'Optionen (Schalter)',
        body: `<dl class="mb-0">
<dt>Firewall aktiv</dt>
<dd>Aktiviert die VM-Firewall für diese VM. Ohne diesen Schalter greifen die VM-Regeln nicht.</dd>
<dt>DHCP erlauben</dt>
<dd>Erlaubt DHCP-Traffic automatisch (typisch für dynamische IPv4-Konfigurationen), auch wenn keine explizite Regel dafür existiert.</dd>
<dt>IP-Filter</dt>
<dd>Erlaubt pro Interface nur definierte Quell-IPs (IPSet <code>ipfilter-&lt;netX&gt;</code>). Ein leeres Set blockiert Traffic auf diesem Interface.</dd>
<dt>MAC-Filter</dt>
<dd>Begrenzt Traffic auf die konfigurierte MAC-Adresse des Interfaces und reduziert Layer-2-Spoofing.</dd>
<dt>NDP (IPv6)</dt>
<dd>Erlaubt Neighbor Discovery für IPv6. Ohne NDP funktionieren viele IPv6-Setups trotz korrekter Regeln nicht sauber.</dd>
<dt>RA / radv (IPv6 Router Advertisements)</dt>
<dd>Erlaubt Router Advertisements (SLAAC). Relevant, wenn Gäste ihre IPv6-Konfiguration automatisch per RA beziehen.</dd>
<dt>Policy eingehend / ausgehend</dt>
<dd>Fallback-Aktion, falls keine Regel matched: <code>ACCEPT</code>, <code>DROP</code> oder <code>REJECT</code>.</dd>
</dl>`,
      },
      {
        heading: 'Empfohlene Defaults (Praxis)',
        body: `<ul class="mb-0 ps-3">
  <li><b>MAC-Filter:</b> meist sinnvoll <b>aktiv</b>, besonders in Multi-Tenant-Umgebungen.</li>
  <li><b>DHCP:</b> aktivieren, wenn VMs IPv4 via DHCP bekommen; bei rein statischer IPv4-Konfiguration optional.</li>
  <li><b>NDP:</b> aktivieren, sobald IPv6 genutzt wird; bei reinem IPv4 kann es aus bleiben.</li>
</ul>
Als sichere Baseline für gemischte Umgebungen gilt häufig: <b>MAC-Filter an</b>, <b>DHCP an</b>, <b>NDP an (bei IPv6)</b>.`,
      },
      {
        heading: 'IP-Filter Hinweise',
        body: `Wenn <b>IP-Filter</b> aktiv ist, müssen je Interface passende IPSet-Einträge gepflegt sein.
Ohne Einträge wirkt das wie eine harte Sperre auf dem betreffenden Interface. Plane daher Rollouts mit Test-VM oder Konsole ein.`,
      },
      {
        heading: 'Regeln & Reihenfolge',
        body: `Regeln werden positionsbasiert ausgewertet. Spezifische Allow/Deny-Regeln sollten vor generischen Regeln stehen.
Nutze Kommentare konsequent, damit die Regelabsicht später nachvollziehbar bleibt.`,
      },
      {
        heading: 'Regelfelder: Ports, Quelle, Ziel',
        body: `<dl class="mb-0">
<dt>Zielport / Quellport</dt>
<dd>Erlaubt sind Service-Namen oder numerische Ports <code>0-65535</code> (gemäss <code>/etc/services</code>).</dd>
<dd>Port-Ranges werden als <code>start:end</code> geschrieben, z.B. <code>80:85</code>.</dd>
<dd>Mehrere Ports oder Ranges können kommagetrennt angegeben werden, z.B. <code>22,80,443,1000:1010</code>.</dd>
<dt>Quelle / Ziel</dt>
<dd>Erlaubt sind einzelne IPs (z.B. <code>10.0.0.5</code>) oder CIDR-Netze (z.B. <code>10.0.0.0/24</code>).</dd>
<dd>Mehrere Einträge können als kommagetrennte Kombination angegeben werden, z.B. <code>10.0.0.5,10.0.1.0/24</code>.</dd>
</dl>`,
      },
    ],
  },
  {
    id: 'firewall',
    title: 'Firewall',
    icon: 'ri-shield-keyhole-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/firewall'],
    summary: 'Firewall-Regelwerke und Zonen.',
    sections: [
      {
        heading: 'In Arbeit',
        body: `Die zentrale Firewall-Verwaltung ist derzeit noch eingeschränkt. Die Firewall einer einzelnen VM wird in der VM-Detailansicht unter <b>Firewall</b> konfiguriert.`,
      },
    ],
  },
  {
    id: 'cloud-init',
    title: 'Cloud-Init',
    icon: 'ri-cloud-line',
    access: 'vm',
    routes: [],
    summary: 'Automatische Erstkonfiguration von Linux- und Windows-Gästen.',
    sections: [
      {
        heading: 'Was ist Cloud-Init?',
        body: `Cloud-Init ist ein Standard zur automatischen Ersteinrichtung einer frisch erstellten VM. Beim ersten Start liest der Gast Konfigurationsdaten vom Cloud-Init-Laufwerk und kann damit Benutzer, SSH-Schlüssel, Passwörter, Hostname, DNS und Netzwerkkonfiguration setzen. Es ist kein Ersatz für ein vollständiges Konfigurationsmanagement wie Ansible, sondern vor allem für die Initialisierung gedacht.`,
      },
      {
        heading: 'Cloud-Init-Laufwerk',
        body: `Proxmox stellt die Daten über ein virtuelles Cloud-Init-Laufwerk bereit. Ohne dieses Laufwerk gibt es keine Cloud-Init-Konfiguration; im Cloud-Init-Tab erscheint dann ein Button zum direkten Hinzufügen. Das Laufwerk enthält keine normale Benutzerdaten-Disk und ersetzt auch kein Betriebssystem-Image.`,
      },
      {
        heading: 'Standardmodus',
        body: `<dl class="mb-0">
<dt>Benutzer / Passwort</dt><dd>Legt den initialen Benutzer fest. SSH-Schlüssel sind für produktive Systeme sicherer als Passwörter.</dd>
<dt>DNS</dt><dd><code>searchdomain</code> setzt die Suchdomain, <code>nameserver</code> die DNS-Server des Gasts.</dd>
<dt>IPv4</dt><dd>DHCP, statische Adresse im CIDR-Format oder keine Konfiguration. Bei mehreren VMs werden statische Adressen hochgezählt.</dd>
<dt>IPv6</dt><dd>DHCP, SLAAC, statisch oder keine Konfiguration.</dd>
</dl>
Die Änderungen werden vom Gast typischerweise beim ersten Boot verarbeitet. Bereits initialisierte Gäste ignorieren manche Werte abhängig von ihrer Cloud-Init-Version oder benötigen eine erneute Initialisierung.`,
      },
      {
        heading: 'Eigene Konfiguration',
        body: `Der Advanced-Modus nimmt eigene <code>user-data</code>-, <code>meta-data</code>-, <code>network-config</code>- und <code>vendor-data</code>-Inhalte an. Standardfelder und eigene Dateien sollten nicht widersprüchlich dieselben Einstellungen setzen. YAML muss syntaktisch gültig sein; beginne bei user-data üblicherweise mit <code>#cloud-config</code>.`,
      },
      {
        heading: 'YAML-Editor',
        body: `Für eigene Cloud-Init-Dateien steht ein Editor mit Syntax-Highlighting und Live-Prüfung bereit
(in den VM-Details und im Erstellungs-Wizard):
<ul class="mb-0 ps-3">
  <li><b>Syntaxprüfung:</b> ungültiges YAML wird direkt im Editor markiert.</li>
  <li><b>Direktiven-Prüfung:</b> unbekannte Schlüssel werden passend zum Gast-OS angemerkt — Linux (cloud-init) und Windows (Cloudbase-Init) unterstützen unterschiedliche Direktiven.</li>
  <li><b>Bausteine:</b> einfügbare Vorlagen für häufige Aufgaben (Benutzer + SSH-Key, Pakete installieren, Befehle ausführen …) für <code>user-data</code> und <code>network-config</code> (netplan v2).</li>
  <li><b>Doku-Links:</b> Verweise auf die offizielle cloud-init- bzw. Cloudbase-Init-Dokumentation direkt im Editor.</li>
</ul>`,
      },
      {
        heading: 'Sicherheit und Grenzen',
        body: `Cloud-Init-Passwörter stehen in der VM-Konfiguration beziehungsweise im erzeugten Config-Drive und sollten deshalb nicht als dauerhafte Geheimnisse behandelt werden. Bevorzuge kurzlebige Zugangsdaten oder SSH-Schlüssel. Änderungen am Cloud-Init-Drive werden erst vom Gast verarbeitet, wenn dieser sie beim Start oder durch eine erneute Cloud-Init-Ausführung einliest.`,
      },
      {
        heading: 'Externe Dokumentation',
        body: `<a href="https://cloudinit.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">Cloud-init Dokumentation</a><br>
<a href="https://pve.proxmox.com/pve-docs/pve-admin-guide.html#qm_cloud_init" target="_blank" rel="noopener noreferrer">Proxmox VE: Cloud-Init Support</a><br>
<a href="https://cloudinit.readthedocs.io/en/latest/reference/modules.html" target="_blank" rel="noopener noreferrer">Cloud-init Module und Verhalten</a>`,
      },
    ],
  },
  {
    id: 'backups-restore',
    title: 'Backups und Restore',
    icon: 'ri-archive-2-line',
    access: 'vm',
    routes: [],
    summary: 'Backup-Auswahl, vollständiger VM-Restore und File-Level-Restore.',
    sections: [
      {
        heading: 'Backup-Liste',
        body: `Der Tab <b>Backups</b> lädt die für die VM sichtbaren Sicherungen über alle Backup-Storages. Die Liste wird automatisch jede Minute aktualisiert, solange der Tab geöffnet ist. Zeitpunkt, Storage, Format und Grösse sind sortierbar; bei vielen Einträgen steht eine Paginierung zur Verfügung. Ein Schloss-Symbol markiert Backups mit aktivem Löschschutz; gerade laufende Sicherungen sind mit „wird erstellt…" gekennzeichnet und können noch nicht wiederhergestellt werden. Pro Backup gibt es zwei klar getrennte Buttons: <b>Datei-Restore</b> (gelb, nur PBS) und <b>VM-Restore</b>.`,
      },
      {
        heading: 'Vollständiger VM-Restore',
        body: `<b>VM-Restore</b> stellt die VM entweder als <b>neue VM</b> wieder her oder überschreibt sie im Modus <b>replace</b>. „Neue VM" erhält eine neue VMID und neue MAC-Adressen. Beim Überschreiben müssen alle aktuellen VM-Daten als unwiderruflich ersetzt betrachtet werden; eine laufende VM kann nicht ersetzt werden. Optional startet die VM nach dem Restore automatisch. Der Restore läuft asynchron und kann je nach Grösse Minuten bis Stunden dauern.`,
      },
      {
        heading: 'Datei-Restore (File-Level)',
        body: `Bei PBS-Backups öffnet <b>Datei-Restore</b> einen Finder-ähnlichen Dateibrowser, um einzelne Dateien oder Verzeichnisse aus dem Backup zu holen — ohne die VM anzurühren. Ordner werden beim Öffnen lazy geladen (der erste Zugriff kann einige Sekunden dauern); enthält das Backup nur eine Disk/Partition, wird sie automatisch geöffnet. System-Laufwerke wie TPM/EFI werden ausgeblendet. Eine Datei wird direkt heruntergeladen; ein Verzeichnis wird als ZIP bereitgestellt. Für klassische VMA-Backups auf Directory/NFS-Storages ist der Datei-Restore nicht verfügbar.`,
      },
      {
        heading: 'Backup ist kein Snapshot',
        body: `Ein Snapshot liegt im Storage-/VM-Kontext und ist vor allem für kurzfristige Rücksprünge vor Änderungen geeignet. Ein Backup ist eine separate Sicherung und schützt besser vor Storage-Ausfall, Fehlkonfiguration oder versehentlichem Löschen. Für wichtige Systeme sollten Aufbewahrung, Test-Restores und ein getrenntes Backup-Ziel vorgesehen werden.`,
      },
      {
        heading: 'Externe Dokumentation',
        body: `<a href="https://pbs.proxmox.com/docs/" target="_blank" rel="noopener noreferrer">Proxmox Backup Server Dokumentation</a><br>
<a href="https://pve.proxmox.com/pve-docs/pve-admin-guide.html#_backup_and_restore" target="_blank" rel="noopener noreferrer">Proxmox VE: Backup und Restore</a>`,
      },
    ],
  },
  {
    id: 'automation',
    title: 'Automatisierung (geplante Aktionen)',
    icon: 'ri-calendar-event-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/scheduled-tasks'],
    summary: 'VM-Aktionen einmalig oder wiederkehrend planen, Verlauf und Admin-Übersicht.',
    sections: [
      {
        heading: 'Tab «Automatisierung»',
        body: `In den VM-Details zeigt der Tab <b>Automatisierung</b> alle für die VM geplanten Automatisierungen mit Beschreibung, Schritten, Zeitplan, Status, nächstem und letztem Lauf. Das <b>⋮-Menü</b> jeder Zeile bietet <b>Jetzt ausführen</b> (startet die Automatisierung nach einer Rückfrage innerhalb einer Minute statt zum nächsten Termin; nur bei geplanten Automatisierungen, ein wiederkehrender Zeitplan bleibt bestehen), <b>Verlauf anzeigen</b>, <b>Bearbeiten</b>, <b>Pausieren/Fortsetzen</b> und <b>Löschen</b> (mit Rückfrage). Status: <b>Geplant</b> (läuft planmässig), <b>Pausiert</b>, <b>Erledigt</b> (einmalige Automatisierung erfolgreich), <b>Fehlgeschlagen</b> bzw. <b>Verpasst</b> (mit Fehlertext). Im Verlauf zeigt <b>Wartet</b> einen Lauf, der noch auf die VM oder einen freien Platz wartet, <b>Läuft</b> einen Lauf, der gerade ausgeführt wird; bricht der Dienst dabei ab (z.B. Neustart), wird er nach spätestens 3 Minuten als «Fehlgeschlagen» mit dem Hinweis «Interrupted» abgeschlossen. Prüfe dann den Zustand der VM, denn Teile der Aktion können bereits ausgeführt sein. Läufe verschiedener VMs laufen gleichzeitig, mehrere Läufe derselben VM nacheinander. Erledigte sind standardmässig ausgeblendet und lassen sich mit dem Schalter <b>Erledigte anzeigen</b> einblenden. Für Templates ist der Tab nicht verfügbar.`,
      },
      {
        heading: 'Anlegen und Bearbeiten',
        body: `Jede Automatisierung besteht aus <b>1 bis 10 Schritten</b>, die nacheinander laufen — so lässt sich eine bestehende Automatisierung später einfach um weitere Schritte ergänzen. <b>Neue Automatisierung</b> führt durch einen Assistenten (Schritte links): <b>1. Schritte</b> (jeder Schritt als eigene Karte mit Aktion und Einstellungen, verschieb- und entfernbar) → <b>2. Zeitplan</b> (inkl. verpasster Ausführung) → <b>3. Beschreibung & Zusammenfassung</b>. «Weiter» prüft jeweils den aktuellen Schritt. Beim <b>Bearbeiten</b> sind dieselben Bereiche als Tabs angeordnet; ein Speichern-Button gilt für alle, Tabs mit Fehlern sind rot markiert.`,
      },
      {
        heading: 'Aktionen pro Schritt',
        body: `Möglich sind Starten, sauber Herunterfahren (optional danach Hardstop), Neu starten, Pausieren, Fortsetzen, <b>Snapshot erstellen</b> (Name «&lt;Präfix&gt;JJJJMMTT-hhmmss» in UTC, optional inkl. RAM), <b>Snapshots aufräumen</b> (löscht Snapshots, deren Name mit dem Präfix beginnt, optional nur solche älter als X Tage) sowie die destruktiven Aktionen <b>Stoppen (hart)</b> und <b>VM löschen</b>, die markiert sind und vor dem Speichern bestätigt werden müssen. Schlägt ein Schritt fehl, werden die folgenden nicht ausgeführt — ausser du schaltest bei diesem Schritt <b>Bei Fehler trotzdem weitermachen</b> ein (siehe unten). Ob die Aktion ausgeführt werden darf (z.B. Snapshot-Recht), prüft der Automatisierungs-Dienst beim Speichern.`,
      },
      {
        heading: 'Snapshot-Präfix',
        body: `Der Präfix wird <b>genau so verwendet, wie du ihn eingibst</b>. Es wird nichts ergänzt, auch kein «-».
<dl class="mb-0">
<dt>Snapshot erstellen</dt><dd>Der Name ist Präfix + Zeitstempel. Mit <code>nightly-</code> entsteht <code>nightly-20261024-020000</code>, mit <code>nightly</code> dagegen <code>nightly20261024-020000</code>. Pflichtfeld, 1 bis 25 Zeichen (Buchstabe am Anfang, dann Buchstaben, Ziffern, «_» oder «-»). Proxmox erlaubt höchstens 40 Zeichen pro Snapshot-Name, 15 davon belegt der Zeitstempel.</dd>
<dt>Snapshots aufräumen</dt><dd>Gelöscht werden Snapshots, deren Name genau mit dem Präfix beginnt, optional nur die älter als X Tage. Gib den ganzen Präfix ein, den du willst, zum Beispiel <code>nightly-</code>. Bis 40 Zeichen.</dd>
<dt>Leerer Präfix</dt><dd>Beim Aufräumen darf der Präfix leer sein. Dann zählen <b>alle</b> Snapshots der VM, auch von Hand angelegte. Ohne Alter werden also ALLE Snapshots gelöscht. Das gilt als destruktiv und muss vor dem Speichern bestätigt werden.</dd>
</dl>
<p class="mb-0 mt-2">Früher hat der Automatisierungs-Dienst immer ein «-» angehängt. Bestehende Automatisierungen wurden dabei umgestellt: Ihr gespeicherter Präfix hat jetzt das «-» am Ende, sie machen also dasselbe wie vorher.</p>`,
      },
      {
        heading: 'Bei Fehler trotzdem weitermachen',
        body: `Bei einer Abfolge mit mehreren Schritten lässt sich <b>pro Schritt</b> festlegen, dass es auch dann weitergeht, wenn genau dieser Schritt fehlschlägt. Beispiel: <i>Herunterfahren → Snapshot → Starten</i>. Schlägt der Snapshot fehl, wird die VM trotzdem wieder gestartet, statt ausgeschaltet liegen zu bleiben. Der Schalter erscheint bei jedem Schritt ausser dem letzten.
<p class="mb-0 mt-2">Der Lauf gilt am Ende trotzdem als <b>fehlgeschlagen</b>: Im Verlauf stehen alle Schritte mit Ergebnis, und der Fehlertext nennt den übersprungenen Schritt. Ein Schritt ohne diesen Schalter beendet die Abfolge wie bisher.</p>`,
      },
      {
        heading: 'Wartezeit pro Schritt',
        body: `Jeder Schritt hat eine optionale <b>Wartezeit</b> (5 Minuten bis 2 Stunden). Du gibst sie in <b>Sekunden oder Minuten</b> ein, die Einheit wählst du neben dem Feld. Leer gilt der <b>Standard</b>, der im Formular mit dem genauen Wert steht.
<dl class="mb-0">
<dt>Herunterfahren, Stoppen (hart), Neu starten</dt><dd>So lange wartet Proxmox auf das Gastsystem. Standard: <b>5 Minuten (300 Sekunden)</b>. Für Anfragen und die Prüfung danach kommt eine kurze Reserve dazu, bei «Neu starten» zusätzlich Zeit für den Start. Ein gesetzter Wert hat Vorrang vor dem Shutdown-Timeout der VM-Konfiguration.</dd>
<dt>Alle anderen Aktionen</dt><dd>Maximale Wartezeit für den ganzen Schritt (VM-Suche, Anfragen, Proxmox-Task und Ergebnisprüfung; bei «Snapshots aufräumen» die ganze Aufräumaktion, bei «VM löschen» inklusive HA-Bereinigung und Stoppen). Standard: <b>15 Minuten (900 Sekunden)</b>, bei «Snapshot erstellen» inklusive RAM <b>30 Minuten (1800 Sekunden)</b>.</dd>
</dl>
<p class="mt-2">Nur beim <b>sauberen Herunterfahren mit anschliessendem Hardstop</b> gibt es eine zweite Zeit: die <b>Wartezeit für den harten Stopp</b> (10 Sekunden bis 2 Stunden, Standard <b>2 Minuten (120 Sekunden)</b>). Sie beginnt erst, wenn das saubere Herunterfahren nicht gelungen ist.</p>
<p class="mb-0">Läuft die Wartezeit ab, endet nur das Warten: Ein bereits gestarteter Proxmox-Task kann weiterlaufen, der Schritt gilt als fehlgeschlagen und weitere Schritte laufen nur mit «Bei Fehler trotzdem weitermachen». Jeder Schritt hat seine eigene Wartezeit, ein ganzer Lauf darf höchstens 4 Stunden dauern (danach startet kein weiterer Schritt, der Lauf gilt als fehlgeschlagen). Bereits gespeicherte Automatisierungen mit der früheren zweiten Zeit (nur per API gesetzt) werden beim Bearbeiten in die eine Wartezeit übernommen.</p>`,
      },
      {
        heading: 'Zeitplan',
        body: `<b>Einmalig</b> zu einem Zeitpunkt in der Zukunft (Lokalzeit) oder <b>wiederkehrend</b>: täglich, wöchentlich an gewählten Wochentagen, monatlich an einem Tag, alle X Stunden oder alle X Minuten — jeweils mit Uhrzeit. Für Spezialfälle steht ein <b>eigener Cron-Ausdruck</b> mit 5 Feldern zur Verfügung (Minute Stunde Monatstag Monat Wochentag, z.B. «0 3 * * 1» = jeden Montag 03:00). Wiederkehrende Zeitpläne gelten in der Zeitzone Europe/Zurich. Ein neuer Zeitpunkt plant einen bereits beendeten einmaligen Task erneut ein.`,
      },
      {
        heading: 'Verpasste Ausführungen',
        body: `Läuft der Automatisierungs-Dienst zum geplanten Zeitpunkt nicht (z.B. Wartung), entscheidet die Einstellung «Verpasste Ausführung»: <b>nachholen, wenn höchstens N Minuten verspätet</b> (Standard 60), <b>immer nachholen</b> oder <b>nie nachholen</b>. Übersprungene Läufe erscheinen im Verlauf als «Verpasst».`,
      },
      {
        heading: 'Verlauf',
        body: `Der Button <b>Verlauf</b> neben «Neue Automatisierung» öffnet alle Ausführungen der VM — auch von inzwischen gelöschten Automatisierungen — mit geplantem Zeitpunkt, Start, Dauer, Ergebnis (Erfolgreich/Fehlgeschlagen/Verpasst) und Meldung (pro Schritt). «Verlauf anzeigen» im ⋮-Menü einer Zeile öffnet ihn vorgefiltert auf diese Automatisierung. Filterbar nach Ergebnis; ältere Einträge per «Ältere laden».`,
      },
      {
        heading: 'Admin-Übersicht',
        access: 'admin',
        body: `Unter <b>Virtuelle Infrastruktur → Automatisierungen</b> sehen Admins alle geplanten Aktionen über alle VMs und Cluster (Filter nach Cluster, enthaltenem Schritt, Status, aktiv/pausiert, Ersteller sowie Freitextsuche; Erledigte per Schalter einblendbar) und können sie pausieren, bearbeiten oder löschen. Der Tab <b>Verlauf</b> zeigt alle Ausführungen, neueste zuerst — mit dem Filter «Fehlgeschlagen» die letzten Fehler. Der VM-Link öffnet direkt den Automatisierungs-Tab der VM.`,
      },
    ],
  },
  {
    id: 'vm-parameters',
    title: 'VM-Parameter erklärt',
    icon: 'ri-settings-4-line',
    access: 'vm',
    routes: [],
    summary: 'Bedeutung der wichtigsten QEMU- und Proxmox-Einstellungen.',
    sections: [
      {
        heading: 'CPU und Machine Type',
        body: `<dl class="mb-0">
<dt>Sockets / Cores</dt><dd>Virtuelle CPU-Topologie. Die Gesamtzahl der vCPUs ergibt sich aus Sockets × Cores.</dd>
<dt>CPU-Modell</dt><dd><code>host</code> reicht die Host-CPU-Features weitgehend an den Gast durch und bietet gute Performance. Das kann Migrationen zwischen unterschiedlichen CPU-Generationen einschränken.</dd>
<dt>Nested Virtualization</dt><dd>Erlaubt es, in der VM selbst wieder VMs zu betreiben (zum Beispiel Hyper-V, WSL2, Docker Desktop). Beim CPU-Typ <code>host</code> ist sie in Proxmox standardmässig aktiv. Neue VMs erhalten sie deaktiviert (<code>flags=-nested-virt</code>); die Prüfung «Abweichungen von Standards» meldet VMs, bei denen sie aktiv ist, und kann sie ausschalten. Eine Änderung wirkt nach dem nächsten Neustart der VM.</dd>
<dt>Machine Type</dt><dd>Definiert den virtuellen Chipsatz und Gerätebus. <code>q35</code> ist der moderne Standard. Windows-VMs können auf eine konkrete q35-Version gepinnt werden.</dd>
<dt>BIOS / UEFI</dt><dd>SeaBIOS ist klassisches BIOS, OVMF ist UEFI. UEFI benötigt eine EFI-Disk; Secure Boot zusätzlich passende Schlüssel und Gastunterstützung.</dd>
</dl>`,
      },
      {
        heading: 'RAM und Sicherheit',
        body: `<dl class="mb-0">
<dt>Memory</dt><dd>Maximaler Arbeitsspeicher der VM in MiB.</dd>
<dt>Ballooning</dt><dd>Erlaubt dem Host, RAM über einen Balloon-Treiber dynamisch zurückzufordern. Der Gast muss den Treiber unterstützen. Im vManager ist das Minimum standardmässig 75% des RAM; beim Ändern des RAM wird es automatisch im gleichen Verhältnis mitgeführt (Admins können den Wert in den CPU/RAM-Einstellungen frei anpassen).</dd>
<dt>Shares</dt><dd>Relative Gewichtung beim Auto-Ballooning (Proxmox-Default: 1000). <code>0</code> würde das Auto-Ballooning deaktivieren und den RAM auf das Balloon-Minimum fixieren — der vManager setzt diesen Wert daher nicht.</dd>
<dt>Allow KSM</dt><dd>Erlaubt oder verhindert Kernel Samepage Merging. Für die Nexanet-Standards ist <code>allow-ksm=0</code> gesetzt.</dd>
<dt>TPM</dt><dd>Virtueller Sicherheitschip, insbesondere für Windows 11 und bestimmte Verschlüsselungs-/Attestation-Szenarien.</dd>
</dl>`,
      },
      {
        heading: 'Grafik und Geräte',
        body: `<dl class="mb-0">
<dt>VGA</dt><dd><code>virtio</code> nutzt eine paravirtualisierte Grafikkarte; <code>memory=8</code> reserviert 8 MiB Grafikspeicher. Für serielle Server ohne GUI kann <code>serial0</code> sinnvoll sein.</dd>
<dt>Guest Agent</dt><dd>Virtio-Kanal für Informationen und Aktionen innerhalb des Gasts. Die Software muss im Gast installiert und gestartet sein.</dd>
<dt>SCSI-Controller</dt><dd><code>virtio-scsi-single</code> erlaubt moderne SCSI-Geräte und ist Voraussetzung für <code>iothread=1</code> auf den Disks.</dd>
</dl>`,
      },
      {
        heading: 'Offizielle Referenz',
        body: `<a href="https://pve.proxmox.com/pve-docs/qm.conf.5.html" target="_blank" rel="noopener noreferrer">Proxmox qm.conf Referenz</a><br>
<a href="https://pve.proxmox.com/pve-docs/pve-admin-guide.html" target="_blank" rel="noopener noreferrer">Proxmox VE Administrationshandbuch</a>`,
      },
    ],
  },
  {
    id: 'storage-disks',
    title: 'Storage und Festplatten',
    icon: 'ri-hard-drive-3-line',
    access: 'vm',
    routes: [],
    summary: 'Storage-Typen, Disk-Busse und wichtige Laufwerksoptionen.',
    sections: [
      {
        heading: 'Storage-Konzepte',
        body: `Ein Proxmox-Storage ist ein benannter Speicherbereich, auf dem VM-Disks, ISOs, Backups oder Container liegen können. Nicht jeder Storage unterstützt jeden Inhaltstyp. Die verfügbaren Storages und ihre Berechtigungen liefert der Cluster; im Wizard werden nicht geeignete Storages ausgeblendet.`,
      },
      {
        heading: 'Disk-Bus und Controller',
        body: `<dl class="mb-0">
<dt>SCSI</dt><dd>Empfohlener Bus für moderne Disks. Mit VirtIO-SCSI werden gute Performance und erweiterte Optionen erreicht.</dd>
<dt>IDE</dt><dd>Wird unter anderem für CD-ROMs und Cloud-Init-Laufwerke verwendet. IDE ist nicht die bevorzugte Bus-Variante für leistungsintensive Daten-Disks.</dd>
<dt>Cloud-Init</dt><dd>Kein normales Datenlaufwerk, sondern ein vom Gast ausgelesenes Konfigurationsmedium.</dd>
</dl>`,
      },
      {
        heading: 'SSD, Discard und IO-Thread',
        body: `<dl class="mb-0">
<dt>ssd=1</dt><dd>Gibt dem Gast die Disk als SSD bekannt.</dd>
<dt>discard=on</dt><dd>Reicht TRIM/Discard-Anforderungen an den Storage weiter. Zulässige Proxmox-Werte sind <code>on</code> oder <code>ignore</code>; <code>discard=1</code> ist ungültig.</dd>
<dt>iothread=1</dt><dd>Verlagert bestimmte I/O-Arbeiten in einen eigenen Thread. Dafür muss der SCSI-Controller <code>virtio-scsi-single</code> sein.</dd>
</dl>
Grössenänderungen vergrössern in der Regel nur die virtuelle Disk. Das Dateisystem und gegebenenfalls die Partition im Gast müssen anschliessend separat erweitert werden.`,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Fehlerdiagnose',
    icon: 'ri-tools-line',
    access: 'vm',
    routes: [],
    summary: 'Typische Fehlermeldungen verstehen und systematisch eingrenzen.',
    sections: [
      {
        heading: 'VM-Erstellung schlägt fehl',
        body: `Prüfe zuerst den Fehlertext und den Task im Tab <b>Ereignisse</b>. „Parameter verification failed“ deutet meist auf einen ungültigen Proxmox-Parameter hin, zum Beispiel ein falsches Enum oder eine inkompatible Kombination. Bei Disk-Problemen sind Storage, Bus, Controller und Optionen wie <code>discard</code> und <code>iothread</code> die ersten Prüfpunkte.`,
      },
      {
        heading: 'Firewall blockiert Traffic',
        body: `Prüfe die Firewall auf VM-Ebene, die Firewall-Option der Netzwerkkarte und die Input-/Output-Policy. Eine VM-Firewall mit Policy <code>DROP</code> kann eingehenden Traffic ohne passende Allow-Regel blockieren. DHCP, NDP und IP-Filter sind zusätzliche Sonderregeln und müssen zur Netzwerkkonfiguration passen.`,
      },
      {
        heading: 'Cloud-Init wirkt nicht',
        body: `Prüfe, ob ein Cloud-Init-Laufwerk vorhanden ist, ob das Gast-Image Cloud-Init installiert hat und ob die Werte im erwarteten Format stehen. Viele Cloud-Init-Module laufen nur einmal. Logs liegen im Gast typischerweise unter <code>/var/log/cloud-init.log</code> und <code>/var/log/cloud-init-output.log</code>; bei Windows gelten die Logs und Werkzeuge des jeweiligen Cloudbase-Init-Setups.`,
      },
      {
        heading: 'Restore oder Download dauert',
        body: `Backups und File-Level-Restores laufen über Proxmox-Tasks und können bei grossen Datenmengen lange dauern. Ein fehlender <code>content-length</code>-Header bei Verzeichnis-ZIPs ist normal, weil das Archiv on-the-fly erzeugt wird. Nicht mehrfach klicken; den Task-Status und die Browser-Downloads prüfen.`,
      },
    ],
  },
  {
    id: 'iso-images',
    title: 'ISO-Images',
    icon: 'ri-disc-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/iso-images'],
    summary: 'Verwaltung von Installations-ISOs.',
    sections: [
      {
        heading: 'Globale und eigene ISOs',
        body: `Zwei Tabs trennen die Liste: <b>Globale ISOs</b> sind die von uns bereitgestellten Standard-Images,
<b>Eigene ISOs</b> (für Admins: <b>Kundeneigene ISOs</b>) sind selbst hochgeladene Images. Admins sehen im
Kunden-Tab zusätzlich, welchem Kunden ein ISO gehört, und können nach Kunde filtern.`,
      },
      {
        heading: 'Hochladen',
        body: `Lade ISO-Dateien hoch — sie stehen anschliessend cluster-weit als Boot-Medium im VM-Wizard zur Verfügung.
Empfohlene Dateigrössen bis ca. 8 GB. Datei muss <code>.iso</code> sein.`,
      },
      {
        heading: 'Verwendung',
        body: `Beim Erstellen einer VM (manuelle Installation) wählst du das ISO im Schritt „Allgemein" als CD-ROM aus.
Nach der Installation kannst du es im VM-Detail wieder auswerfen. Die Spalte „Verwendet von X VM(s)" zeigt live,
welche VMs ein ISO aktuell eingelegt haben — und aktualisiert sich sofort, wenn du es auswirfst.`,
      },
      {
        heading: 'Löschen',
        body: `Ein ISO kann nur gelöscht werden, wenn keine VM es aktuell eingehängt hat.`,
      },
    ],
  },
  {
    id: 'templates',
    title: 'Templates',
    icon: 'ri-file-copy-2-line',
    access: 'vm',
    routes: [],
    summary: 'Vorlagen aus einer VM erstellen, im Assistenten verwenden, aktualisieren und verrechnen.',
    sections: [
      {
        heading: 'Was ist ein Template?',
        body: `Ein Template ist eine fertig eingerichtete VM, die als Kopiervorlage dient. Es läuft nie und lässt sich nicht starten.
Aus einem Template erstellst du beliebig viele VMs mit demselben Betriebssystem, denselben Programmen und denselben Einstellungen.
Der vManager erstellt dabei immer <b>vollständige Kopien</b> (keine verknüpften Klone): Jede neue VM ist unabhängig vom Template.`,
      },
      {
        heading: 'VM in ein Template umwandeln',
        body: `<ol class="mb-0 ps-3">
  <li>Bereite das Betriebssystem vor: <a href="/help/template-windows">Windows (Sysprep)</a> oder <a href="/help/template-linux">Linux</a>.</li>
  <li>Gib der VM einen sprechenden Namen (Menü der VM: «VM umbenennen»). Der Name der VM wird zum Namen des Templates. Eine Sprachendung wie <code>_DE</code> oder <code>-EN</code> zeigt der Erstellungs-Assistent als Flagge.</li>
  <li>Fahre die VM herunter. Nur ausgeschaltete VMs lassen sich umwandeln.</li>
  <li>Rechtsklick auf die VM in der Hierarchie, «VM in Template umwandeln», Rückfrage mit «In Template umwandeln» bestätigen.</li>
</ol>
Eigene VMs und damit eigene Templates kannst du immer selbst umwandeln.
<b>Achtung:</b> Die VM ist danach keine normale VM mehr. Du kannst sie nur noch zurückwandeln, um Anpassungen am Template zu machen (siehe «Template aktualisieren»), und musst sie danach wieder zum Template machen. Eine vollwertige VM bekommst du, indem du eine neue VM aus dem Template erstellst.`,
      },
      {
        heading: 'Templates erkennen',
        body: `Im Baum haben Templates ein eigenes Symbol (zwei übereinanderliegende Blätter) und keinen Statuspunkt. In der Kachel- und Listenansicht sind sie ausgeblendet; der Schalter «Templates» über der Übersicht blendet sie am Ende der Liste ein.`,
      },
      {
        heading: 'Im Assistenten verwenden',
        body: `Im Erstellungs-Assistenten («VM erstellen») steht beim Feld «Typ» die Option <b>Ab eigenem Template</b>, sobald es auf dem Cluster mindestens ein eigenes Template gibt. Die Vorlagen der Admins erscheinen unter «Ab Nexanet Template».`,
      },
      {
        heading: 'Template aktualisieren',
        body: `Ein Template ändert sich nicht. Für Updates wandelst du es wieder in eine VM um (Rechtsklick, «Template in VM umwandeln», Rückfrage «Zur VM umwandeln»), startest sie, aktualisierst sie, fährst sie herunter und wandelst sie erneut in ein Template um. Bereits erstellte VMs bleiben dabei unverändert, weil sie vollständige, unabhängige Kopien sind. Bereite das Betriebssystem vor dem erneuten Umwandeln wieder vor (Sysprep beziehungsweise Linux-Bereinigung). Die zurückgewandelte VM ist nur für Anpassungen am Template gedacht und keine vollwertige VM (auf Ceph sind zum Beispiel keine Snapshots möglich). Eine normale VM erstellst du immer neu aus dem Template.`,
      },
      {
        heading: 'Template löschen',
        body: `Ein Template löschst du genau wie eine VM. Das Löschen ist endgültig; mit dem Template entfallen auch seine Kosten.`,
      },
      {
        heading: 'Verrechnung',
        body: `Ein Template wird wie eine gestoppte VM verrechnet: Grundpreis pro VM und Speicher (Festplatten in Blöcken zu 10 GB, aufgerundet), dazu zugewiesene IP-Adressen. CPU, RAM und Windows-Lizenz werden nicht verrechnet. Die Preis-Pille in der Detailansicht des Templates zeigt die Aufschlüsselung.`,
      },
    ],
  },
  {
    id: 'template-windows',
    title: 'Windows-Template vorbereiten (Sysprep)',
    icon: 'ri-windows-line',
    access: 'vm',
    routes: [],
    summary: 'Windows vor dem Umwandeln in ein Template mit Sysprep verallgemeinern.',
    sections: [
      {
        heading: 'Warum Sysprep?',
        body: `Jede aus einem Template erstellte VM ist eine Kopie. Ohne Vorbereitung haben alle Kopien dieselbe Computer-SID, denselben Computernamen und dieselben Maschinenkennungen. Das führt zu Konflikten, vor allem in Domänen. <b>Sysprep</b> entfernt diese systemspezifischen Daten (Verallgemeinern) und lässt Windows beim ersten Start jeder Kopie neu einrichten.`,
      },
      {
        heading: 'Vorbereitung',
        body: `<ul class="mb-0 ps-3">
  <li>Installiere alle Windows-Updates und die Programme, die jede Kopie braucht. Starte danach neu und prüfe, dass keine Updates mehr ausstehen.</li>
  <li>Installiere die <b>virtio-Treiber</b> und den <b>QEMU Guest Agent</b> (der Datenträger- und Netzwerktreiber ist für den Start der Kopien nötig).</li>
  <li>Optional: <b>Cloudbase-Init</b> installieren, damit die Kopien beim ersten Start Hostname, Benutzer und Netzwerk aus der Cloud-Init-Konfiguration beziehen (siehe Hilfe «Cloud-Init»).</li>
  <li>Entferne persönliche Daten, Browser-Verläufe und Anmeldedaten. Ein Snapshot vor Sysprep erleichtert die Rückkehr bei einem Fehler.</li>
</ul>`,
      },
      {
        heading: 'Sysprep ausführen',
        body: `Öffne eine Eingabeaufforderung als Administrator und starte:
<pre class="mb-2"><code>C:\\Windows\\System32\\Sysprep\\sysprep.exe /generalize /oobe /shutdown</code></pre>
<ul class="mb-0 ps-3">
  <li><code>/generalize</code> entfernt die systemspezifischen Daten, <code>/oobe</code> startet die Kopie mit der Erstkonfiguration, <code>/shutdown</code> fährt die VM danach herunter.</li>
  <li>Starte die VM nach Sysprep <b>nicht mehr</b>. Ein Start würde die Erstkonfiguration auslösen und die Vorbereitung verbrauchen. Wandle die ausgeschaltete VM direkt in ein Template um.</li>
  <li>Schlägt Sysprep fehl, steht der Grund in <code>C:\\Windows\\System32\\Sysprep\\Panther\\setuperr.log</code>. Häufige Ursachen sind für einzelne Benutzer installierte Store-Apps und ein bereits mehrfach verwendetes Sysprep (die Anzahl ist begrenzt).</li>
</ul>`,
      },
      {
        heading: 'Hinweise',
        body: `Die Aktivierung und Lizenz gehören zum Windows-System und werden von Sysprep nicht ersetzt: Prüfe die Lizenzbedingungen für deine Kopien. Bei Windows Server wird die Lizenz zusätzlich verrechnet, sobald die VM läuft (siehe Hilfe «Templates», Verrechnung). Die Anleitung des Herstellers findest du unter <a href="https://learn.microsoft.com/windows-hardware/manufacture/desktop/sysprep--generalize--a-windows-installation" target="_blank" rel="noopener noreferrer">Sysprep (Generalize) a Windows installation</a>.`,
      },
    ],
  },
  {
    id: 'template-linux',
    title: 'Linux-Template vorbereiten',
    icon: 'ri-terminal-box-line',
    access: 'vm',
    routes: [],
    summary: 'Linux vor dem Umwandeln in ein Template bereinigen.',
    sections: [
      {
        heading: 'Warum vorbereiten?',
        body: `Eine Kopie erbt alles aus dem Template: die Maschinenkennung (<code>machine-id</code>), die SSH-Hostschlüssel, Netzwerkeinstellungen und Protokolle. Gleiche Maschinenkennungen und Schlüssel führen zu Konflikten (zum Beispiel doppelte DHCP-Adressen) und sind ein Sicherheitsrisiko. Linux kennt kein einheitliches Werkzeug wie Sysprep: Du bereinigst die VM von Hand oder mit einem Skript.`,
      },
      {
        heading: 'Vorbereitung',
        body: `<ul class="mb-0 ps-3">
  <li>Aktualisiere das System und installiere die Programme, die jede Kopie braucht.</li>
  <li>Installiere den <b>QEMU Guest Agent</b> (<code>qemu-guest-agent</code>) und, wenn du die Kopien beim ersten Start einrichten willst, <b>cloud-init</b> (siehe Hilfe «Cloud-Init»).</li>
  <li>Mache vorher einen Snapshot, falls du die Bereinigung rückgängig machen willst.</li>
</ul>`,
      },
      {
        heading: 'Bereinigen (Beispiel Debian/Ubuntu)',
        body: `Als root, danach die VM mit <code>poweroff</code> herunterfahren (kein Neustart, damit die Bereinigung erhalten bleibt):
<pre class="mb-2"><code># Pakete und Protokolle aufräumen
apt-get clean
journalctl --rotate; journalctl --vacuum-time=1s
find /var/log -type f -exec truncate -s 0 {} \\;

# Eindeutige Kennungen entfernen (werden beim ersten Start neu erzeugt)
truncate -s 0 /etc/machine-id
rm -f /var/lib/dbus/machine-id &amp;&amp; ln -s /etc/machine-id /var/lib/dbus/machine-id
rm -f /etc/ssh/ssh_host_*

# cloud-init zurücksetzen (nur wenn installiert)
cloud-init clean --logs

# Verläufe löschen
history -c; rm -f ~/.bash_history
poweroff</code></pre>
<ul class="mb-0 ps-3">
  <li><b>SSH-Hostschlüssel:</b> Auf Ubuntu und mit cloud-init erzeugt der Gast sie beim ersten Start neu. Ohne cloud-init richtest du die Erzeugung ein, zum Beispiel mit <code>dpkg-reconfigure openssh-server</code> in einem Start-Dienst, sonst startet der SSH-Dienst in der Kopie nicht.</li>
  <li><b>Andere Distributionen:</b> Die Idee ist dieselbe (Maschinenkennung, SSH-Schlüssel, Protokolle, Paketcache). Die Befehle unterscheiden sich: <code>dnf clean all</code> bei RHEL, Rocky und AlmaLinux, <code>zypper clean --all</code> bei SUSE.</li>
  <li><b>Netzwerk:</b> Entferne feste MAC-Adressen in Netzwerk-Konfigurationen (zum Beispiel <code>/etc/udev/rules.d/70-persistent-net.rules</code>), sonst bekommt die Kopie keine Netzwerkkarte.</li>
</ul>`,
      },
      {
        heading: 'Danach',
        body: `Wandle die ausgeschaltete VM in ein Template um (siehe Hilfe «Templates»). Starte sie vorher nicht mehr, sonst entstehen die Kennungen neu und du musst die Bereinigung wiederholen.`,
      },
    ],
  },
  {
    id: 'kunden',
    title: 'Kunden',
    icon: 'ri-team-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/kunden'],
    summary: 'Kunden- und Mandantenverwaltung.',
    sections: [
      { heading: 'In Arbeit', body: 'Kapitel folgt sobald die Funktion verfügbar ist.' },
    ],
  },
  {
    id: 'preise',
    title: 'Preise',
    icon: 'ri-price-tag-3-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/preise'],
    summary: 'Preislisten und Verrechnung.',
    sections: [
      { heading: 'In Arbeit', body: 'Kapitel folgt sobald die Funktion verfügbar ist.' },
    ],
  },
  {
    id: 'pricing-plans',
    title: 'Server Preispläne',
    icon: 'ri-price-tag-3-line',
    access: 'admin',
    routes: ['^/virtual-infrastructure/pricing-plans'],
    summary: 'Preispläne mit Definitionen, Cluster-Overrides und Storage-Preisen (Admin).',
    sections: [
      {
        heading: 'Pläne und Definitionen',
        body: `Ein <b>Preisplan</b> bündelt die Konditionen für Kunden. Pro Plan gibt es <b>Definitionen</b> mit
Gültigkeitsdatum (<i>validFrom</i>): Es gilt immer die jüngste Definition, deren Datum nicht in der Zukunft liegt —
so lassen sich Preisänderungen im Voraus erfassen. Eine Definition ohne Cluster ist der <b>Default</b>;
zusätzliche Definitionen pro Cluster übersteuern ihn.`,
      },
      {
        heading: 'Preisfelder',
        body: `Preise sind in CHF pro Monat hinterlegt: Basispreis pro VM, CPU-Core, RAM-GB, Storage pro 10-GB-Block,
Public-IP und Windows-Lizenz. <b>Storage:</b> Der Standardpreis pro 10 GB gilt für alle Storages; pro Storage-ID
können Overrides hinterlegt werden. Verrechnet wird immer auf volle 10-GB-Blöcke aufgerundet (23 GB → 30 GB).`,
      },
      {
        heading: 'Kunden-Zuordnung',
        body: `Unter <b>Kundenkonditionen</b> wird pro Kunde festgelegt, welcher Plan ab welchem Stichtag gilt.
Ein Plan kann erst gelöscht werden, wenn ihm keine Kunden mehr zugeordnet sind.`,
      },
    ],
  },
  {
    id: 'customer-pricing',
    title: 'Preise & Konditionen',
    icon: 'ri-money-dollar-circle-line',
    access: 'vm',
    routes: ['^/virtual-infrastructure/customer-pricing'],
    summary: 'Eigene Konditionen bzw. Preisplan-Zuordnungen — inkl. VM-Preisanzeige.',
    sections: [
      {
        heading: 'Meine Konditionen',
        body: `Als Benutzer zeigt die Seite <b>Preise &amp; Konditionen</b> deine eigenen Konditionen pro
Kundennummer: den zugewiesenen Preisplan und alle Preise (Grundpreis, CPU, RAM, Speicherplatz inkl.
allfälliger Storage-spezifischer Preise, öffentliche IPs, Windows-Lizenz) in CHF pro Monat — samt
Gültigkeit: „gilt bis auf Weiteres" oder bis zum nächsten angekündigten Wechsel. Angekündigte
Preisänderungen erscheinen mit Datum, geänderte Positionen sind hervorgehoben.`,
      },
      {
        heading: 'Kundenkonditionen (Admin)',
        access: 'admin',
        body: `Admins sehen stattdessen die Zuordnungs-Ansicht: welcher Preisplan für welchen Kunden ab welchem
Stichtag gilt. Ein Planwechsel wird als neue Zuordnung mit Stichtag angelegt — der neue Plan muss sich vom
zum Stichtag wirksamen Plan unterscheiden; alte Einträge bleiben als Historie bestehen.
<br><br>Die Spalte <b>Verrechnung</b> legt pro Kunde fest, ob für ihn Rechnungen erstellt werden. Ausgeschaltet
(mit Rückfrage) steht der Schalter auf „deaktiviert“; beim Aufklappen des Kunden steht, wer die Einstellung
wann zuletzt geändert hat. Kunden sehen diese Einstellung nicht. Der <b>Rechnungslauf</b> überspringt solche Kunden
(Grund „Verrechnung deaktiviert“).
<br><br>Die Spalte <b>Akonto</b> legt die Akonto-Pflicht pro Kunde fest (standardmässig aus, Ändern mit Rückfrage).
Massgebend ist die Einstellung im Moment des Rechnungslaufs: Eingeschaltet enthält die Rechnung zusätzlich ein Akonto
für den Folgemonat; ausgeschaltet wird ein bestehendes Akonto ein letztes Mal gutgeschrieben, ein neues entfällt.`,
      },
      {
        heading: 'VM-Preisanzeige',
        body: `In der VM-Detailansicht zeigt eine Preis-Pill die monatlichen Kosten der VM nach aktuellem Preisplan —
aufgeschlüsselt nach Basis, CPU, RAM, Storage, Public-IP und Windows-Lizenz. Gestoppte VMs und Templates werden
günstiger gerechnet (CPU/RAM/Windows entfallen). Beim Bearbeiten der VM zeigt die Entwurfsleiste den neuen Preis
inklusive Differenz; auch der Erstellungs-Wizard rechnet live mit.`,
      },
    ],
  },
  {
    id: 'customer-settings',
    title: 'Einstellungen & Wiederverkauf',
    icon: 'ri-settings-3-line',
    access: 'financial',
    routes: [
      '^/virtual-infrastructure/settings',
      '^/virtual-infrastructure/customer-settings',
      '^/virtual-infrastructure/reseller-pricing',
      '^/virtual-infrastructure/end-customers',
      '^/virtual-infrastructure/reseller-export',
    ],
    summary: 'Kundeneinstellungen, Wiederverkaufskonditionen, End-Kunden und Weiterverrechnung.',
    sections: [
      {
        heading: 'Einstellungen',
        body: `Auf der Seite <b>Einstellungen</b> aktivierst du den <b>Wiederverkauf (Reseller)</b>. Nach der Aktivierung
erscheinen im Menü die Seiten <b>Wiederverkauf</b> (Wiederverkaufskonditionen) und <b>Endkunden</b> (Abschnitt Einstellungen) sowie
<b>Weiterverrechnung</b> (Abschnitt Administratives).
Beim Deaktivieren bleiben gespeicherte Pläne und End-Kunden erhalten, können aber nicht mehr bearbeitet oder
exportiert werden.`,
      },
      {
        heading: 'Wiederverkaufskonditionen',
        body: `Hier definierst du Preispläne für deine End-Kunden. Der Typ hängt an der einzelnen <b>Definition</b>
(Stichtag) und ist auf der Zeitachse mischbar — z.B. bis März Marge, ab April eigene Preise: <b>Eigene Preise</b> —
Verkaufspreise pro Komponente in CHF/Monat, inkl. Cluster-spezifischer Definitionen und Storage-Ausnahmen (leere
Felder erben von der Default-Definition, in Marge-Perioden von der Marge); <b>Marge auf Einkauf</b> — prozentualer
Aufschlag pro Komponente auf deinen Einkaufspreis (nur auf der Default-Ebene; Cluster-Definitionen sind immer
Preis-Definitionen und können eine geltende Marge gezielt übersteuern). Genau ein Plan kann <b>Default</b> sein: Er
gilt für alle End-Kunden ohne eigene Zuweisung. Stichtage sind immer der 1. eines Monats, frühestens der laufende
Monat — Definitionen mit Stichtag in der Vergangenheit sind unveränderlich, der Typ einer bestehenden Definition
ebenfalls (für einen Wechsel eine neue Definition mit neuem Stichtag anlegen).`,
      },
      {
        heading: 'Endkunden',
        body: `Auf der Seite <b>Endkunden</b> erfasst du deine End-Kunden als Stammdaten: Pflicht sind nur die
<b>Kennung</b> und ein <b>Preisplan</b>; Adresse, Kontakt und Notizen sind optional. Die Kennung ist frei wählbar
(1–100 Zeichen aus Buchstaben, Ziffern, «.» und «-» — kein «_») und entspricht dem Teil vor dem ersten «_» der
Verzeichnisse direkt unter deinem Hauptverzeichnis (z.B. «KA123453B_Mueller_AG» → KA123453B); der Abgleich erfolgt
ohne Beachtung von Gross-/Kleinschreibung. Verrechnet wird nur, wer erfasst ist — nicht zugeordnete Verzeichnisse
landen im Export unter «Ohne Kundenzuordnung». Die Kennung
ist nach dem Erstellen unveränderlich (Abrechnungsschlüssel). Der Preisplan ist <b>historisiert</b>: Wechsel erfolgen
per «Planwechsel» immer auf den 1. eines Monats, frühestens ab dem laufenden Monat — nie rückwirkend. Pro
Abrechnungsmonat gilt die Zuweisung mit dem jüngsten Stichtag; ohne Eintrag greift der Default-Plan. Geplante Wechsel
lassen sich löschen, vergangene nicht (und die letzte Zuweisung eines End-Kunden ebenfalls nicht). End-Kunden mit
Abrechnungshistorie können nicht gelöscht werden. Beim Erfassen kann der vManager das passende
<b>Kundenverzeichnis</b> gleich mit anlegen (Schalter, standardmässig aktiv; ausgegraut, wenn es bereits existiert) —
benannt als «Kennung_Firma» bzw. «Kennung_Vorname_Nachname», unzulässige Zeichen werden automatisch umgewandelt.`,
      },
      {
        heading: 'Weiterverrechnung (Export)',
        body: `Die Seite <b>Weiterverrechnung</b> erstellt Excel- oder PDF-Berichte pro End-Kunde (mehrere End-Kunden →
ZIP), wahlweise pro Monat oder Jahr und in vier Detailgraden. <b>Weiterverkauf</b> rechnet mit deinen VK-Preisen bzw.
der Marge auf den Einkauf; <b>Einkauf</b> liefert die EK-Rohdaten (inkl. Gruppe «Ohne Kundenzuordnung»). Die Berichte
sind neutral gebrandet — mit deinem Firmennamen statt nexanet-Logo. Die Plan-Historie wird automatisch berücksichtigt.`,
      },
      {
        heading: 'Kundeneinstellungen (Admin)',
        access: 'admin',
        body: `Admins verwalten die Einstellungen pro Kunde auf der Seite <b>Kundeneinstellungen</b> (Kunde wählen →
Wiederverkauf ein-/ausschalten). Der Tab <b>Wiederverkaufskonditionen</b> zeigt die Preispläne, Definitionen und
End-Kunden des Kunden — bewusst nur einsehbar, nicht editierbar; der Export kann auch als Admin ausgelöst werden.`,
      },
    ],
  },
  {
    id: 'invoices',
    title: 'Rechnungen',
    icon: 'ri-file-list-3-line',
    access: 'financial',
    routes: ['^/administratives/rechnungen'],
    summary: 'Eigene Rechnungen inkl. Bezahlstatus und Positionen.',
    sections: [
      {
        heading: 'Überblick',
        body: `Die Seite zeigt alle Rechnungen deiner Kundennummern (Status <b>offen</b> oder <b>bezahlt</b>;
Entwürfe und Stornos erscheinen nicht). Bei offenen Rechnungen wird der noch offene Betrag
(Brutto minus bereits bezahlt) ausgewiesen. Ein Klick auf eine Rechnung zeigt direkt das
Original-Rechnungs-PDF eingebettet an.`,
      },
      {
        heading: 'Original-PDF',
        body: `Das PDF wird beim Öffnen frisch erzeugt — das kann 1–3 Sekunden dauern. Über
<b>In neuem Tab öffnen</b> bzw. das PDF-Symbol in der Liste lässt es sich auch in einem
eigenen Tab anzeigen (z.B. zum Herunterladen oder Drucken).`,
      },
    ],
  },
  {
    id: 'netbox-sync',
    title: 'NetBox-Sync',
    icon: 'ri-book-2-line',
    access: 'admin',
    routes: ['^/administratives/netbox-sync'],
    summary: 'Fehler der automatischen VM-Dokumentation in NetBox.',
    sections: [
      {
        heading: 'Was wird synchronisiert?',
        body: `vmonitor überträgt stündlich alle QEMU-VMs der Cluster mit aktivem NetBox-Sync nach NetBox — VM, Disks,
Interfaces inkl. VLAN und MAC sowie den Tenant (Kunde). Die VM heisst in NetBox «&lt;VM-Name&gt; (&lt;VMID&gt;)».
In den VM-Details führt der Link <b>NetBox</b> neben der VM-ID direkt zur Dokumentation der VM (nur Admins).`,
      },
      {
        heading: 'Seite „Kontrolle → NetBox-Sync“',
        body: `Oben steht pro Cluster eine Kachel: <b>grau</b> = kein Sync, <b>grün</b> = keine Fehler, <b>gelb</b> = offene
Fehler, <b>rot</b> = Cluster-Sync abgebrochen (dann wird der Cluster gar nicht gesynct). Ein Klick filtert die Liste auf
den Cluster. Die Box <b>Fehlt in NetBox</b> listet VLANs und Tenants, die in NetBox angelegt werden müssen, mit Anzahl
betroffener VMs. Die Fehlerliste lässt sich nach Status (offen/behoben/alle), Cluster, Fehlertyp, Objektart und
Freitext filtern; die technische Meldung klappt pro Zeile auf, «In NetBox öffnen» führt zur VM in NetBox.`,
      },
      {
        heading: 'Offen, behoben, veraltet',
        body: `Jeder Fehler erscheint nur einmal, egal wie oft er auftritt, und wird nach einem erfolgreichen Lauf
automatisch geschlossen — es gibt nichts zu quittieren. <b>Veraltet</b> heisst: offen, aber seit über 2 Stunden nicht
mehr gesehen — der Sync läuft dann vermutlich nicht. Die Zahl offener Fehler erscheint als Badge am Unterpunkt
<b>NetBox-Sync</b> (rot bei Cluster-Abbruch oder veralteten Fehlern, sonst gelb).`,
      },
    ],
  },
  {
    id: 'invoicing-run',
    title: 'Rechnungslauf',
    icon: 'ri-send-plane-line',
    access: 'admin',
    routes: ['^/administratives/invoicing-run'],
    summary: 'Monatsrechnungen der VM-Infrastruktur im ERP erstellen.',
    sections: [
      {
        heading: 'Was wird verrechnet?',
        body: `Abgerechnet wird immer der <b>letzte abgeschlossene Monat</b> (Zürcher Zeit) — am 25.09. also der August.
Einen anderen Monat wählen kann man nicht. Pro Kunde und Monat entsteht genau <b>eine</b> Rechnung; ein erneuter Lauf
überspringt bereits verrechnete Kunden. Die Rechnungen landen im ERP mit Status «preparing» und werden dort geprüft
und versendet. Jede Rechnung hat bis zu drei Positionen: <b>Kosten des Monats</b> (VMs + Kundenpositionen),
<b>Abzüglich Akonto</b> (Gutschrift des im Vormonat verrechneten Akontos) und <b>Akonto für den Folgemonat</b> (Stand
Monatsende auf einen vollen Monat hochgerechnet, auf 5 Rappen gerundet). Akontos gibt es nur bei Kunden mit
<b>Akonto-Pflicht</b> (Einstellung in den Kundenkonditionen, Badge «Akonto» in der Vorschau): Beim ersten Monat
Kosten + neues Akonto, danach Kosten − altes + neues Akonto; wird die Pflicht entfernt, folgt eine letzte Gutschrift
ohne neues Akonto, danach nur noch Kosten.`,
      },
      {
        heading: 'Vorschau und Lauf',
        body: `Die Seite zeigt zuerst eine <b>Vorschau</b> aller Kunden — es wird nichts geschrieben. Oben stehen Abrechnungsmonat,
Rechnungsdatum und die Summen der zu erstellenden Rechnungen. Jede Zeile lässt sich aufklappen und zeigt die Positionen
wie auf der Rechnung. Filter: Alle, Zu verrechnen, Übersprungen, Probleme; Kunden ohne Kosten und ohne Akonto
(«Nichts zu verrechnen») sind standardmässig ausgeblendet. <b>Rechnungslauf starten</b> erstellt nach Rückfrage alle
Rechnungen; danach zeigt die Tabelle das Ergebnis pro Kunde (erstellt mit ERP-Nummer, übersprungen, fehlgeschlagen mit
Fehlertext). Über das «⋮»-Menü einer Zeile lässt sich auch nur ein einzelner Kunde verrechnen bzw. ein
fehlgeschlagener Kunde erneut versuchen. Fehlt Konfiguration im ERP (z.B. Einheit/Produkte), ist der Lauf gesperrt.`,
      },
      {
        heading: 'Gründe fürs Überspringen',
        body: `<b>Grau</b> (normal): bereits verrechnet, Verrechnung deaktiviert (Schalter in den Kundenkonditionen), nichts zu
verrechnen. <b>Gelb</b> (später erneut): Abrechnungsdaten noch nicht final, Rechnung wird gerade erstellt.
<b>Rot</b> (korrigieren): Positionen ohne Preis (siehe Kontrolle → Abrechnung), Kunde nicht im ERP, MWST-Satz fehlt im
ERP, Betrag über dem ERP-Limit von CHF 999'999.99.`,
      },
      {
        heading: 'Probleme, Rechnungen und Protokoll',
        body: `Würde eine Rechnung negativ, wird die Akonto-Gutschrift so weit gekürzt, dass sie CHF 0 beträgt — der nicht
gutgeschriebene Rest erscheint rot und muss <b>manuell geklärt</b> werden. Die Box <b>Probleme</b> listet solche Fälle
sowie nicht erstellte Rechnungen (fehlgeschlagen bzw. in Arbeit). Ihre Anzahl steht als rotes Badge am Menüpunkt
<b>Rechnungslauf</b> und am Unterpunkt <b>Kontrolle → Rechnungslauf</b> und lässt das Kontrolle-Icon pulsieren. Der Tab
<b>Rechnungen</b> zeigt alle erstellten Rechnungen (Filter Monat, Kunde, Status, nur Problemfälle; übersprungene Kunden
erscheinen dort nicht), der Tab <b>Protokoll</b> alle Läufe — ein Klick zeigt das Ergebnis pro Kunde.`,
      },
      {
        heading: 'Rechnung neu erstellen',
        body: `Wurde eine erstellte Rechnung im ERP gelöscht, erstellt der Button <b>Rechnung neu erstellen</b> (Tab
<b>Rechnungen</b>, nach Rückfrage) sie neu. Das geht nur, wenn sie im ERP wirklich weg ist — sonst erscheint eine Meldung
und es wird nichts angelegt; doppelte Rechnungen sind ausgeschlossen. Kosten, Akonto-Gutschrift und neues Akonto bleiben
unverändert; die Rechnung erhält eine neue ERP-Nummer und das heutige Rechnungsdatum, die MWST richtet sich nach heutigem
Datum und Land des Kunden. Jede Neuerstellung steht im Protokoll als «Neuerstellung». Fehlgeschlagene Rechnungen holt
stattdessen der normale Rechnungslauf nach.`,
      },
    ],
  },
  {
    id: 'billing',
    title: 'Abrechnung',
    icon: 'ri-bill-line',
    access: 'financial',
    routes: ['^/administratives'],
    summary: 'Monatliche VM-Abrechnung: Übersicht, VM-Kosten und Kontrolle.',
    sections: [
      {
        heading: 'Monatsmodell',
        body: `Abgerechnet wird pro Kalendermonat. Ein Monat ist entweder <b>offen</b> (Month-to-date; wächst täglich,
Rechenlauf jeweils um 04:00 Uhr) oder <b>final</b> (unveränderlich). Beträge werden sekundengenau anteilig gerechnet:
Betrag = Menge × Monatspreis × (Dauer / Monatslänge).`,
      },
      {
        heading: 'Komponenten',
        body: `<dl class="mb-0">
<dt>Basis</dt><dd>Grundpreis pro VM — unabhängig davon, ob die VM läuft.</dd>
<dt>CPU / RAM</dt><dd>Pro Core bzw. GB — nur während die VM läuft; Stoppzeiten erscheinen als Zeilen mit Menge 0.</dd>
<dt>Storage</dt><dd>Pro 10-GB-Block (aufgerundet) — unabhängig vom VM-Status.</dd>
<dt>Public IP</dt><dd>Pro IP-Zuweisung, die der VM zugeordnet und verrechnungsaktiv ist (siehe IP-Zuweisungen) — eine Zeile pro IP. Vom Guest-Agent gemeldete Adressen sind nicht verrechnungsrelevant.</dd>
<dt>Windows-Lizenz</dt><dd>Für Windows-Server-VMs, während sie laufen.</dd>
</dl>`,
      },
      {
        heading: 'Kundenpositionen ohne VM',
        body: `Positionen, die direkt am Kunden hängen (z.B. IP-Zuweisungen ohne VM-Bindung oder mit unbekannter VM),
werden als <b>Kundenpositionen (ohne VM)</b> verrechnet. Sie sind in allen Totalen enthalten (Gesamtkosten =
VM-Kosten + Kundenpositionen) und erscheinen in der Berichts-Vorschau sowie im PDF/Excel-Bericht als eigener
Abschnitt — allerdings nur, wenn der Bericht den ganzen Kunden umfasst (ohne Unterverzeichnis-Auswahl oder
Gruppierung). Kunden ohne VMs, aber mit solchen Positionen, erscheinen ebenfalls in der Abrechnung.`,
      },
      {
        heading: 'Seite „Abrechnung" (Kundenansicht)',
        body: `Die Abrechnungsseite zeigt die Kosten eines Kunden. Als normaler Benutzer landest du direkt in der
Ansicht deines eigenen Kunden. Zwei Tabs:
<dl class="mb-0">
<dt>Übersicht</dt><dd>Kostenentwicklung der letzten 12 Monate als gestapelte Balken pro Komponente, darunter die
Monatstabelle mit VMs, Cores, RAM, Storage und den Kosten je Komponente (inkl. <b>Kosten IPs</b> — IP-Adressen an VMs
und ohne VM-Zuweisung — und Grundpreis). Ein Klick auf einen Monat öffnet «Kosten &amp; Bericht» mit diesem Monat.
Beim laufenden Monat wird zusätzlich eine Hochrechnung auf den ganzen Monat angezeigt.</dd>
<dt>Kosten &amp; Bericht</dt><dd>Aufgebaut wie die Seite „VM-Kosten“, aber nur für diesen Kunden. Oben wählst du den
<b>Zeitabschnitt</b> (Monat oder Jahr, Standard: laufender Monat), die <b>Informationstiefe</b> („Nur Total pro Monat“,
„Total pro VM“, „Pro VM, aufgeteilt nach Kostenart“, „Pro VM und Kostenart mit allen Zeitabschnitten“) und die
<b>Gliederung</b> der Anzeige (keine, nach Verzeichnis, nach Verzeichnis und Unterverzeichnis — mit Zwischentotalen).
Bei Wiederverkäufern kommt die Auswahl <b>Endkunde</b> dazu („Alle“, je Endkunde mit Kosten im Zeitraum oder
„Eigene (ohne Endkunde)“ — letzteres nur als Anzeige). Die Tabelle zeigt die verrechneten VMs; jede Zeile lässt sich
aufklappen und zeigt die Kosten pro Komponente, jede Komponente wiederum ihre Zeitabschnitte. Die Buttons „PDF“ und
„Excel“ erstellen den Bericht sofort; mit „Ein Bericht pro Endkunde (ZIP)“ bzw. „Ein Bericht pro Verzeichnis (ZIP)“
entsteht pro Endkunde bzw. Verzeichnis ein eigener Bericht. Laufende Perioden sind als vorläufig
gekennzeichnet; in der Jahresansicht sind die Zeilen nicht aufklappbar (im Bericht sind die Details enthalten).</dd>
</dl>`,
      },
      {
        heading: 'Kundenübersicht (Admin)',
        access: 'admin',
        body: `Admins sehen als Einstieg eine Tabelle aller Kunden; über die Kundensuche lässt sich jeder Kunde direkt
öffnen. Die <b>Zeitbasis der Kennzahlen</b> (Monat/Jahr, Standard: laufender Monat) bestimmt, auf welchen Zeitraum sich
die Werte beziehen: Anzahl VMs, CPU Cores, RAM GB, Disk GB (Stand Ende der Zeitbasis), Total Umsatz und Differenz zur
Vorperiode; beim laufenden Monat zusätzlich die Prognose (Standard-Sortierung: Prognose absteigend). Ein Klick auf
einen Spaltenkopf sortiert (erneuter Klick kehrt die Richtung um), ein Klick auf den Kunden öffnet dessen Detailansicht.
Warn-Markierungen verweisen auf Problemfälle in der Kontrolle.`,
      },
      {
        heading: 'Seite „VM-Kosten"',
        body: `Alle VM-Positionen eines Monats mit Filtern (Monat, Kunde, Cluster, Status, VMID) und wählbarer
Gruppierung (Kunde, Cluster, Verzeichnis-Unterebenen). Jede Zeile lässt sich aufklappen: Die Details sind nach
Komponente gruppiert und zeigen die einzelnen Zeitsegmente mit Zeitraum, Dauer, Menge, Preis und Betrag.
Gedimmte Zeilen mit Menge 0 sind Stoppzeiten. Bei Kundenwechsel im Monat erscheint dieselbe VM zweimal —
einmal pro Kunde. Ein Warn-Symbol markiert Positionen ohne berechenbaren Betrag (z.B. fehlender Preisplan).`,
      },
      {
        heading: 'Seite „Kontrolle → Abrechnung" (Admin)',
        access: 'admin',
        body: `Der Menüpunkt <b>Kontrolle</b> hat die Unterseiten <b>Abrechnung</b>, <b>NetBox-Sync</b> und
<b>Rechnungslauf</b> (je eigenes Hilfe-Kapitel). Die Abrechnungskontrolle listet Auffälligkeiten des Monats: VMs ohne Kundenzuordnung, ohne Preisplan
oder mit Konfigurationsfehlern — mit Direktlinks in die VM-Kosten-Ansicht zur Klärung. Der Unterpunkt <b>Abrechnung</b>
zeigt als rotes Badge die Anzahl VMs ohne Preisplan im laufenden Monat. Solange bei einem der Unterpunkte ein
Badge steht, leuchtet das Icon <b>Kontrolle</b> in der Hauptnavigation rot und pulsiert.`,
      },
      {
        heading: 'Rechnungen',
        body: `Die effektiv gestellten Rechnungen (inkl. Original-PDF) findest du auf der separaten Seite
<b>Rechnungen</b> — siehe eigenes Hilfe-Kapitel.`,
      },
    ],
  },
  {
    id: 'kubernetes',
    title: 'Kubernetes',
    icon: 'ri-ship-2-line',
    access: 'kubernetes',
    routes: ['^/virtual-infrastructure/kubernetes'],
    summary: 'Verwaltung der Kubernetes-Cluster.',
    sections: [
      {
        heading: 'Überblick',
        body: `Die Seite listet die Kubernetes-Cluster auf der virtuellen Infrastruktur. Cluster können erstellt,
bearbeitet und auf eine neue Version aktualisiert werden; die Vorgänge laufen asynchron im Hintergrund.`,
      },
    ],
  },
  {
    id: 'support',
    title: 'Support-Tickets',
    icon: 'ri-customer-service-2-line',
    routes: ['^/support/tickets'],
    summary: 'Support-Anfragen direkt aus der Anwendung erstellen und verfolgen.',
    sections: [
      {
        heading: 'Ticket erstellen',
        body: `Über das Headset-Symbol oben rechts in der Kopfzeile öffnet sich der Ticket-Dialog (für Feedback und Ideen
auch über <b>Deine Idee oder dein Feedback teilen</b> auf der Startseite — Betreff und Priorität sind dann vorbelegt):
Betreff, Beschreibung und Priorität erfassen, optional bis zu 5 Datei-Anhänge (insgesamt max. 10 MB)
anhängen — und absenden. Die Beschreibung lässt sich über den <b>Diktieren</b>-Button auch per Mikrofon
einsprechen (Spracherkennung des Browsers; in Chrome, Edge und Safari verfügbar). Nach dem Erstellen wird
die Ticketnummer angezeigt; die weitere Kommunikation läuft per E-Mail. Voraussetzung ist eine hinterlegte
E-Mail-Adresse im Benutzerkonto. Das Erstellen von Tickets ist Kunden-Benutzern vorbehalten (nicht Admins).`,
      },
      {
        heading: 'Screenshot und VM-Referenz',
        body: `Mit dem Schalter <b>„Screenshot der aktuellen Ansicht mitschicken"</b> wird beim Absenden automatisch
ein Bildschirmfoto der aktuellen Browser-Ansicht angehängt — der Ticket-Dialog selbst ist darauf nicht zu sehen.
Wird das Ticket aus einer VM-Detailansicht heraus erstellt, kann die betroffene VM per Schalter direkt
referenziert werden — der Support sieht dann sofort, um welches System es geht.
Zusätzlich werden zur besseren Einordnung die aktuelle Seite, eine allfällig geöffnete VM sowie
Browser- und Anzeige-Informationen automatisch an die Nachricht angehängt.`,
      },
      {
        heading: 'Meine Tickets',
        body: `Die Seite <b>Meine Tickets</b> (Menübereich Support) listet alle eigenen Support-Tickets — unabhängig
davon, ob sie über das Portal, per E-Mail oder telefonisch eröffnet wurden (Zuordnung über die E-Mail-Adresse
des Benutzerkontos). Die Liste bietet Volltextsuche (Titel, Nummer und Inhalt), Filter nach Status, Priorität
und Erstellungszeitraum, sortierbare Spalten und Paginierung. Standardmässig werden aktive Tickets
angezeigt; geschlossene und zusammengeführte blendet der Statusfilter bei Bedarf ein.`,
      },
      {
        heading: 'Ticket öffnen und antworten',
        body: `Ein Klick auf ein Ticket öffnet den kompletten Gesprächsverlauf: eigene Nachrichten und
Support-Antworten chronologisch, inklusive Anhängen (Bilder mit Vorschau, alle Dateien per Klick ladbar).
Solange das Ticket nicht geschlossen ist, kann direkt geantwortet werden — mit Nachricht und optionalen
Datei-Anhängen (max. 5 / 10 MB); das Ticket wird dadurch beim Support wieder als offen markiert.
Ist das Anliegen erledigt, schliesst <b>Ticket schliessen</b> (oben rechts) das Ticket selbst — optional mit einer
Abschlussnachricht an den Support, die im selben Schritt angehängt wird.
Geschlossene Tickets sind nur noch lesbar — für neue Anliegen bitte ein neues Ticket erstellen.`,
      },
    ],
  },
  {
    id: 'shortcuts',
    title: 'Tastenkürzel',
    icon: 'ri-keyboard-line',
    routes: [],
    summary: 'Nützliche Shortcuts für effizientes Arbeiten.',
    sections: [
      {
        heading: 'Allgemein',
        body: `<dl class="mb-0">
<dt><code>?</code></dt><dd>Hilfe-Drawer öffnen/schliessen.</dd>
<dt><code>Esc</code></dt><dd>Drawer und Modals schliessen.</dd>
</dl>`,
      },
      {
        heading: 'VM-Suche',
        access: 'vm',
        body: `<dl class="mb-0">
<dt><code>Leertaste</code></dt><dd>Bestätigt den aktuellen Suchbegriff als Token.</dd>
<dt><code>↑</code>/<code>↓</code>, <code>Enter</code>/<code>Tab</code></dt><dd>Vorschlag wählen und übernehmen.</dd>
<dt><code>Backspace</code></dt><dd>Im leeren Feld: letztes Token zur Bearbeitung zurückholen.</dd>
<dt><code>Esc</code></dt><dd>Vorschläge schliessen.</dd>
</dl>`,
      },
    ],
  },
];

// Zugriff je nach Berechtigung des eingeloggten Users.
const hasAccess = (access) => {
  if (!access) return true;
  const g = store.getters;
  switch (access) {
    case 'vm': return Boolean(g['auth/canAccessVm']);
    case 'admin': return Boolean(g['auth/canAccessAdmin']);
    case 'financial': return Boolean(g['auth/canAccessFinancial']);
    case 'kubernetes': return Boolean(g['auth/canAccessKubernetes']);
    default: return true;
  }
};

// Nur Kapitel/Abschnitte, die der User auch nutzen kann.
export function visibleDocs() {
  return docs
    .filter((c) => hasAccess(c.access))
    .map((c) => ({ ...c, sections: (c.sections || []).filter((s) => hasAccess(s.access)) }))
    .filter((c) => c.sections.length > 0);
}

// Find the chapter that best matches the given route path.
export function chapterForRoute(path) {
  const p = String(path || '');
  const visible = visibleDocs();
  for (const c of visible) {
    if (!Array.isArray(c.routes)) continue;
    for (const r of c.routes) {
      try {
        if (new RegExp(r).test(p)) return c;
      } catch (_) { /* invalid regex – ignore */ }
    }
  }
  return visible[0] || docs[0];
}

export function searchDocs(query) {
  const visible = visibleDocs();
  const q = String(query || '').trim().toLowerCase();
  if (!q) return visible;
  return visible
    .map((c) => {
      const matchedSections = c.sections.filter((s) =>
        `${s.heading} ${s.body}`.toLowerCase().includes(q)
      );
      const titleMatch = `${c.title} ${c.summary || ''}`.toLowerCase().includes(q);
      if (titleMatch && matchedSections.length === 0) return c;
      if (matchedSections.length > 0) return { ...c, sections: matchedSections };
      return null;
    })
    .filter(Boolean);
}
